import { ensureYahooReviewPrepared } from './CampaignLaunchWorkflow/yahoo/prepareYahooReview';
import { YAHOO_BULK_PLACEHOLDER } from './CampaignLaunchWorkflow/yahoo/yahooBulkEdit';
import {
  isYahooResult, buildYahooReviewRows, summarizeYahooResult,
  YAHOO_GROUP_COLS, YAHOO_GROUP_KEYS, YAHOO_EDITABLE, YAHOO_FIELD_EDITORS,
  YAHOO_READ_ONLY_REASONS, YAHOO_LIST_FIELDS,
} from './CampaignLaunchWorkflow/yahoo/yahooReview';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, getApiUrl } from '../utils/apiConfig';
import { useAccount } from '../contexts/AccountContext';
import { getUserData } from '../utils/userUtils';
import { summarizeTtdResult, buildTtdReviewRows } from './CampaignLaunchWorkflow/ttd/ttdRowBuilders';
import { summarizeDv360Result, buildDv360ReviewRows } from './CampaignLaunchWorkflow/dv360/dv360RowBuilders';
import { summarizeReviewCtsResult, buildGtReviewRows, gtCtsFailure } from './CampaignLaunchWorkflow/groundtruth/gtRowBuilders';
import { isGtLauncherResult, gtLauncherToCtsReview } from './CampaignLaunchWorkflow/groundtruth/gtLauncherAdapter';
import {
  isGadsLauncherResult,
  gadsResultSubtype,
  summarizeGadsResult,
  buildGadsReviewRows,
} from './CampaignLaunchWorkflow/gads/gadsRowBuilders';
import {
  gadsGroupCols,
  gadsGroupKeys,
  gadsEditableFields,
  gadsListFields,
  gadsReadOnlyReasons,
  GADS_FIELD_EDITORS,
  GADS_ROW_LABEL,
  GADS_SUBTYPE_LABELS,
  gadsSubtype,
} from './CampaignLaunchWorkflow/gads/gadsConstants';
import {
  isFbLauncherResult,
  summarizeFbResult,
  buildFbReviewRows,
} from './CampaignLaunchWorkflow/fb/fbRowBuilders';
import {
  isFbPlatform,
  FB_GROUP_COLS,
  FB_GROUP_KEYS,
  FB_EDITABLE,
  FB_FIELD_EDITORS,
  FB_LIST_FIELDS,
  FB_ROW_LABEL,
  FB_PLATFORM_LABEL,
  FB_READ_ONLY_REASONS,
} from './CampaignLaunchWorkflow/fb/fbConstants';
import { AgentSidebar } from './CampaignLaunchWorkflow/agent/AgentSidebar';
// Cell detail panel + the editable-field allow-lists, shared verbatim with
// CLW. The audiences modal, geo modal, and every scalar-field editor on this
// page ARE CLW's — same component, same round-trip field mappings — so this
// page can take over from CLW without the two drifting apart.
import { CellDetailPanel } from './CampaignLaunchWorkflow/CellDetailPanel';
import ReviewGrid from './CampaignLaunchWorkflow/ReviewGrid';
import { findGtLaunchedWorkspace } from './GtLaunchedWorkspace/gtLaunchState';
import { PlanNotices } from './CampaignLaunchWorkflow/PlanNotices';
import useReviewGridEdits from './CampaignLaunchWorkflow/useReviewGridEdits';
import LoadingCard from './LoadingCard';
import { ConsolidatedReviewSummary, LOOKUP_PATHS } from './CampaignLaunchWorkflow/CampaignLaunchWorkflow';
import ChangeLogButton from './CampaignLaunchWorkflow/ChangeLogPanel';
import SourceFilesButton from './CampaignLaunchWorkflow/SourceFilesPanel';
import './CampaignLaunchWorkflow/CampaignLaunchWorkflow.css';
import {
  GROUP_COLS,
  GROUP_KEYS,
  DV360_EDITABLE,
  TTD_EDITABLE,
  GT_EDITABLE,
  DV360_FIELD_EDITORS,
  TTD_FIELD_EDITORS,
  DV360_LIST_FIELDS,
  TTD_LIST_FIELDS,
  GT_LIST_FIELDS,
  DV360_READ_ONLY_REASONS,
  TTD_READ_ONLY_REASONS,
} from './CampaignLaunchWorkflow/clwGridConstants';
import { GT_GROUP_COLS, GT_GROUP_KEYS, GT_FIELD_EDITORS, GT_STATIC_OPTIONS, GT_READ_ONLY_REASONS } from './CampaignLaunchWorkflow/groundtruth/gtConstants';
import { gtLookupTypesFor } from './CampaignLaunchWorkflow/groundtruth/gtFieldDescriptor';
import { applyGtConfigEdits, applyLauncherResultEdits, droppedGtEdits, gtEditWarnings } from './CampaignLaunchWorkflow/reviewEdits';
import { buildLaunchedEditRequests } from './CampaignLaunchWorkflow/buildLaunchedEditRequests';
import { dispatchLaunchedEdits } from './CampaignLaunchWorkflow/dispatchLaunchedEdits';
import { useMmgyClwReviewerSession } from '../hooks/useMmgyClwReviewerSession';
import { useBulkEditorSession } from '../hooks/useBulkEditorSession';
import { BulkEditPanel } from './CampaignLaunchWorkflow/agent/BulkEditPanel';
import { buildBulkDigest, rowIdSet, serializeListFields } from './CampaignLaunchWorkflow/agent/bulkDigest';
import { BULK_TAB, QA_AGENT_TAB_ENABLED, isSelectableMode } from './CampaignLaunchWorkflow/agent/agentTabs';
import { previewReadyToShow } from './CampaignLaunchWorkflow/agent/previewGate';
import { buildGtBulkFeedItems } from './CampaignLaunchWorkflow/agent/gtChatFeed';
import { gtBulkPlaceholder } from './CampaignLaunchWorkflow/agent/gtBulkPlaceholder';
import { OptimizationRunGate } from './CampaignLaunchWorkflow/agent/OptimizationRunGate';
import { OptimizationRunBar } from './CampaignLaunchWorkflow/agent/OptimizationRunBar';
import { useOptimizationSession, OPT_PHASE } from '../hooks/useOptimizationSession';
import { validateStaging, mergeEdits, mergeListOps, mergeRowOps } from './CampaignLaunchWorkflow/agent/bulkOps';
import { useAccountSettings } from '../hooks/useAccountSettings';
import { isEnabledForPlatform, isEnabledForAnyPlatform } from '../utils/accountSettingPlatformList';
import './CampaignLaunchWorkflow/CampaignLaunchWorkflow.css';
import './LaunchCampaignDetails.css';

/**
 * LaunchCampaignDetails — campaign details variant of the CLW page.
 *
 * Rendered on `/campaign/v3/start` for campaigns opened from the Launch board
 * (`from=launch`) or with the `frommock` param; every other entry point gets
 * the full Campaign Launch Workflow. Pixel-faithful port of the two
 * campaign-detail views from the Stellair Campaign Launch design (index.html
 * v20), selected by the campaign's stage (launch-list), with planning-review's
 * alreadyLaunched flag as the tiebreaker when the stage is unknown:
 *  - dsp_ready → DSP reconcile view ("DSP: Ready for review"): header +
 *    summary strip, QA Summary Stats / Discrepancies / Full ad-group grid.
 *  - plan_ready / plan_in_review → plan review view: header + parse-stat
 *    strip, QA Summary Stats (format breakdown + plan QA table) / Flagged
 *    Items / Full ad-group grid. Approving still happens in CLW — the
 *    primary button links there.
 *  - no parsed plan (incl. processing campaigns and unsupported platforms) →
 *    empty state linking to CLW.
 *  - `frommock=demo` → the design mock's full demo dataset (reconcile view).
 *
 * Data sources — the same ones CLW's review pane uses:
 *  - `/api/v2/campaigns/{id}/planning-review` → launcherResult (TTD/DV360/
 *    Google Ads Search, PMax and Demand Gen) or ctsReview (GT), flattened with
 *    CLW's own row builders. Feeds the header, summary strip, breakdown/QA
 *    tables, flagged items (row annotations), and the full ad-group grid (all
 *    PLAN-side values).
 *  - `/api/v2/campaigns/{id}/stage` → campaign name + destination + stage.
 *
 * DSP-side values (live budgets/spend, discrepancies, match counts) have no
 * backend yet — in the reconcile view DSP columns mirror the plan and the
 * page reads "DSP sync pending".
 */

// ── Design-mock demo dataset ─────────────────────────────────────────
const DEMO_TOTAL_AD_GROUPS = 184;

// sev: high|med|low. compare columns are [label, value, isMismatch] rows.
const DEMO_DISCREPANCIES = [
  {
    id: 'budget', sev: 'high',
    text: <>Daily budget reduced in DSP: <em>PKG 7 · CRM Lists - Display Banners</em> &amp; <em>PKG 12 · Geofarm Conquest</em></>,
    diff: { plan: '$420/day', dsp: '$380/day' },
    dspHeadNote: '· last synced 4m ago',
    plan: [['Daily budget:', '$420.00', true], ['Total budget:', '$12,600.00'], ['Pacing:', 'Even daily'], ['Bid:', '$6.50 CPM']],
    dsp: [['Daily budget:', '$380.00', true], ['Total budget:', '$11,400.00'], ['Pacing:', 'Even daily'], ['Bid:', '$6.50 CPM']],
    explain: 'Trafficker manually capped daily budget at $380 (the account-default daily ceiling). To run the plan as ordered, the account-level cap needs to be lifted or the IO total reduced by 9.5%.',
    push: { label: '↑ Push plan to DSP ($420/day)', msg: 'Pushed $420/day cap to GroundTruth · 2 ad groups updated' },
    accept: { label: '↓ Accept DSP value ($380/day) — update plan', msg: 'Plan updated to $380/day · IO total reduced to $46,800' },
  },
  {
    id: 'flight', sev: 'high',
    text: <>Flight end date truncated in DSP: <em>PKG 5 · Display Conquest</em></>,
    diff: { plan: 'Apr 30', dsp: 'Apr 15' },
    plan: [['Start:', 'Apr 1, 2026'], ['End:', 'Apr 30, 2026', true], ['Duration:', '30 days'], ['Budget:', '$14,400']],
    dsp: [['Start:', 'Apr 1, 2026'], ['End:', 'Apr 15, 2026', true], ['Duration:', '15 days'], ['Budget:', '$14,400']],
    explain: 'DSP insertion order was created with a default 15-day IO window. The full 30-day flight from the plan was not applied. Campaign will go dark Apr 16 unless corrected immediately.',
    push: { label: '↑ Extend DSP flight to Apr 30' },
  },
  {
    id: 'overpace', sev: 'high',
    text: <>Budget overpaced: <em>PKG 9 · Programmatic Audio</em></>,
    diff: { plan: '$8,400', dsp: '$10,332 spent' },
    plan: [['Total budget:', '$8,400', true], ['Pacing:', 'Even daily'], ['Daily cap:', '$280/day'], ['Status:', 'In flight']],
    dsp: [['Total spent:', '$10,332', true], ['Pacing:', 'ASAP'], ['Daily cap:', 'None set'], ['Overspend:', '+23%']],
    explain: 'Pacing was set to ASAP instead of Even daily, and no daily cap was applied. The line item has already exceeded the planned IO total. Pause and reconcile with finance before resuming.',
    push: { label: '↑ Set pacing to Even daily + add $280 daily cap' },
  },
  {
    id: 'audience', sev: 'med',
    text: <>Audience mismatch: <em>PKG 19 · Lookalike - Mobile Video</em></>,
    diff: { plan: 'Sams_LAL_2pct_Mar26', dsp: 'Sams_LAL_5pct_Feb26' },
    plan: [['Audience:', 'Sams_LAL_2pct_Mar26.csv', true], ['Seed size:', '1,240,000'], ['Refreshed:', 'Mar 15, 2026 (10 days ago)'], ['Match rate:', '88%']],
    dsp: [['Audience:', 'Sams_LAL_5pct_Feb26.csv', true], ['Seed size:', '3,180,000'], ['Refreshed:', 'Feb 20, 2026 (33 days ago)'], ['Match rate:', '92%']],
    explain: 'The 2% LAL was uploaded after launch but never re-attached to PKG 19. The DSP is still serving against the older 5% LAL — broader reach but lower-quality match. Recommend pushing the planned 2% LAL.',
    push: { label: '↑ Attach planned audience to DSP', msg: 'Attached Sams_LAL_2pct_Mar26.csv to PKG 19 · DSP applying within 15 min' },
    accept: { label: '↓ Update plan to match DSP', msg: 'Plan updated to reference Sams_LAL_5pct_Feb26.csv' },
  },
  {
    id: 'freqcap', sev: 'med',
    text: <>Frequency cap mismatch: <em>PKG 11 · CTV Connected</em></>,
    diff: { plan: '3/day', dsp: '5/day' },
    plan: [['Frequency cap:', '3 imp/day', true], ['Cap type:', 'Per user'], ['Window:', '24 hours'], ['Tactic:', 'CTV']],
    dsp: [['Frequency cap:', '5 imp/day', true], ['Cap type:', 'Per user'], ['Window:', '24 hours'], ['Tactic:', 'CTV']],
    explain: 'DSP default frequency cap of 5/day was not overridden during trafficking. Client spec requires no more than 3 impressions per day to avoid ad fatigue on connected TV.',
    push: { label: '↑ Set frequency cap to 3/day in DSP' },
    accept: { label: '↓ Accept 5/day — update plan' },
  },
  {
    id: 'bidtype', sev: 'med',
    text: <>Bid type changed in DSP: <em>PKG 8 · Mobile Retargeting</em></>,
    diff: { plan: 'CPM $5.40', dsp: 'oCPM' },
    plan: [['Bid type:', 'CPM', true], ['Bid:', '$5.40', true], ['Optimization:', 'Reach'], ['Ad groups:', '22']],
    dsp: [['Bid type:', 'oCPM', true], ['Bid:', 'Dynamic', true], ['Optimization:', 'Conversions'], ['Ad groups:', '22']],
    explain: 'Trafficker switched to oCPM for conversion optimization, overriding the fixed CPM in the plan. This changes spend dynamics and may exceed the planned rate significantly on high-converting inventory.',
    push: { label: '↑ Revert to CPM $5.40' },
    accept: { label: '↓ Accept oCPM — update plan' },
  },
  {
    id: 'dma', sev: 'med',
    text: <>DMA targeting incomplete: <em>PKG 15 · Local Market Push</em></>,
    diff: { plan: '18 DMAs', dsp: '14 DMAs' },
    plan: [['DMA count:', '18', true], ['Markets:', 'All top-20 retail DMAs'], ['Coverage:', 'National + local'], ['Budget share:', '$6,200']],
    dsp: [['DMA count:', '14', true], ['Missing:', 'Dallas, Phoenix, Seattle, Tampa'], ['Coverage:', 'Partial'], ['Budget share:', '$6,200']],
    explain: 'Four DMAs (Dallas, Phoenix, Seattle, Tampa) were not included during DSP geo-targeting setup. Budget allocated to those markets is currently serving nationally instead of locally.',
    push: { label: '↑ Add 4 missing DMAs to DSP' },
  },
  {
    id: 'geo', sev: 'low',
    text: <>DMA exclusion not applied: <em>PKG 4 · Display - National</em></>,
    diff: { plan: 'Exclude HI & AK', dsp: 'Running everywhere' },
    plan: [['Geo:', 'National'], ['Exclusions:', 'HI, AK', true], ['Markets:', '48 contiguous'], ['Note:', 'No shipping to HI/AK']],
    dsp: [['Geo:', 'National'], ['Exclusions:', 'None', true], ['Markets:', '50 states + DC + PR'], ['Spent in HI/AK:', '$214 (0.4% of total)']],
    explain: 'Geo exclusion list was not added in the GroundTruth setup wizard. Small spillover so far ($214). Easy push to DSP.',
    push: { label: '↑ Add HI/AK exclusion in DSP', msg: 'HI & AK exclusion added to PKG 4 · effective on next refresh' },
  },
  {
    id: 'creative', sev: 'low',
    text: <>Creative size absent in DSP: <em>PKG 2 · OLV Pre-roll</em></>,
    diff: { plan: '3 sizes', dsp: '2 sizes' },
    plan: [['15s pre-roll:', '✓ Attached'], ['30s pre-roll:', '✓ Attached'], ['6s bumper:', '✓ Attached', true], ['Total assets:', '3']],
    dsp: [['15s pre-roll:', '✓ Live'], ['30s pre-roll:', '✓ Live'], ['6s bumper:', 'Missing', true], ['Total assets:', '2']],
    explain: 'The 6-second bumper ad was uploaded to Stellair but not trafficked into the DSP line item. Low urgency as the 15s and 30s are running, but bumper inventory is being missed.',
    push: { label: '↑ Upload 6s bumper to DSP' },
  },
  {
    id: 'viewability', sev: 'low',
    text: <>Viewability threshold not set: <em>PKG 6 · Premium Display</em></>,
    diff: { plan: '≥70% viewable', dsp: 'No threshold' },
    plan: [['Viewability:', '≥70% in-view', true], ['Standard:', 'MRC Display'], ['Measured by:', 'IAS'], ['Ad groups:', '31']],
    dsp: [['Viewability:', 'Not set', true], ['Standard:', '—'], ['Measured by:', '—'], ['Ad groups:', '31']],
    explain: 'Viewability filter was not applied during line item setup. Ads may be serving in non-viewable placements, lowering effective reach. Low spend impact but affects brand safety reporting.',
    push: { label: '↑ Apply ≥70% viewability filter in DSP' },
  },
  {
    id: 'impgoal', sev: 'low',
    text: <>Impression goal not set in DSP: <em>PKG 22 · Retargeting Mobile</em></>,
    diff: { plan: '1,200,000 imps', dsp: 'Unlimited' },
    plan: [['Imp goal:', '1,200,000', true], ['CPM:', '$4.20'], ['Est. spend:', '$5,040'], ['Tactic:', 'Mobile retargeting']],
    dsp: [['Imp goal:', 'Not set', true], ['CPM:', '$4.20'], ['Est. spend:', 'Budget-limited only'], ['Tactic:', 'Mobile retargeting']],
    explain: 'Impression cap was not entered during DSP setup. Line item will serve until budget is exhausted regardless of impression volume, which may result in frequency spikes against a narrow retargeting pool.',
    push: { label: '↑ Set impression goal to 1,200,000' },
  },
];

const SEV_META = {
  high: { label: 'High', cls: 'disc-sev-high', dot: 'issue-dot-m' },
  med: { label: 'Medium', cls: 'disc-sev-med', dot: 'issue-dot-m' },
  low: { label: 'Low', cls: 'disc-sev-low', dot: 'issue-dot-q' },
};

const TAG_STYLES = {
  mis2: { background: '#FBE9E7', color: '#BF360C' },
  mis1: { background: '#FFF3E0', color: '#E65100' },
  match: { background: '#E0F4E4', color: '#1E6B33' },
  pending: { background: '#F1F5F9', color: '#64748B' },
};
const DEMO_TACTIC_ROWS = [
  { tag: 'DISP', tagCls: 'cmp-tag-disp', name: 'Display', ag: '48', planB: '$14,200', dspB: '$13,840', dB: '-$360', dBCls: 'bad', planI: '2,840,000', dspI: '2,768,000', dI: '-72,000', dICls: 'bad', status: '2 mismatched', statusStyle: TAG_STYLES.mis2 },
  { tag: 'OLV', tagCls: 'cmp-tag-olv', name: 'Online Video', ag: '62', planB: '$22,100', dspB: '$22,100', dB: '$0', dBCls: 'ok', planI: '2,210,000', dspI: '2,210,000', dI: '0', dICls: 'ok', status: 'All match', statusStyle: TAG_STYLES.match },
  { tag: 'PROX', tagCls: 'cmp-tag-prox', name: 'Proximity / Geofence', ag: '38', planB: '$6,400', dspB: '$6,400', dB: '$0', dBCls: 'ok', planI: '1,280,000', dspI: '1,294,400', dI: '+14,400', dICls: 'neutral', status: '1 mismatched', statusStyle: TAG_STYLES.mis1, mismatch: true },
  { tag: 'CONQ', tagCls: 'cmp-tag-conq', name: 'Competitor Conquesting', ag: '22', planB: '$3,200', dspB: '$3,200', dB: '$0', dBCls: 'ok', planI: '640,000', dspI: '640,000', dI: '0', dICls: 'ok', status: 'All match', statusStyle: TAG_STYLES.match },
  { tag: 'L&L', tagCls: 'cmp-tag-ll', name: 'Lunch & Learn', ag: '14', planB: '$2,500', dspB: '$2,660', dB: '+$160', dBCls: 'neutral', planI: '930,000', dspI: '950,000', dI: '+20,000', dICls: 'neutral', status: 'All match', statusStyle: TAG_STYLES.match },
];
const DEMO_TACTIC_TOTAL = { ag: '184', planB: '$48,400', dspB: '$48,200', dB: '-$200', dBCls: 'bad', planI: '7,900,000', dspI: '7,862,400', dI: '-37,600', dICls: 'bad', status: '3 mismatched' };

const DEMO_SETUP_ROWS = [
  { section: 'Audiences' },
  { name: '1st-party CRM lists attached', plan: '12', dsp: '12', delta: 'Match', ok: true },
  { name: 'Look-alike audiences (live version)', plan: 'Sams_LAL_2pct_Mar26', dsp: 'Sams_LAL_5pct_Feb26', delta: 'Outdated' },
  { name: '3rd-party segments', plan: '14', dsp: '14', delta: 'Match', ok: true },
  { section: 'Geo' },
  { name: 'Geofences active', plan: '2,840', dsp: '2,840', delta: 'Match', ok: true },
  { name: 'DMA targets', plan: '112', dsp: '112', delta: 'Match', ok: true },
  { name: 'National exclusions (HI/AK)', plan: 'Excluded', dsp: 'Not applied', delta: 'Missing' },
  { section: 'Creative' },
  { name: 'Display banners trafficked', plan: '24', dsp: '24', delta: 'Match', ok: true },
  { name: 'Video assets trafficked (15s / 30s)', plan: '18 / 12', dsp: '18 / 12', delta: 'Match', ok: true },
  { name: 'Click URLs verified', plan: '100%', dsp: '100%', delta: 'Match', ok: true },
  { section: 'Bid & pacing' },
  { name: 'Daily budget caps respected', plan: 'No cap', dsp: '$380/day (2 PKGs)', delta: 'Reduced' },
  { name: 'Frequency caps', plan: '3/day · 12/week', dsp: '3/day · 12/week', delta: 'Match', ok: true },
  { name: 'Bidding strategy', plan: 'Even pacing', dsp: 'Even pacing', delta: 'Match', ok: true },
];

const DEMO_QA_REPORTS = {
  creatives: {
    badge: '2 mismatched',
    cols: [
      { key: 'name', label: 'Ad group' },
      { key: 's1', label: '300×250', paired: true },
      { key: 's2', label: '320×50', paired: true },
      { key: 's3', label: '320×480', paired: true },
      { key: 'tot', label: 'Total', paired: true },
      { key: 'status', label: 'Status' },
    ],
    rows: [
      { name: 'PKG 1 · Location Audience – Loyalty · Sams Club', plan: [2, 2, 2, 6], dsp: [2, 2, 2, 6] },
      { name: 'PKG 1 · On Premise – In Store · Sams Club', plan: [2, 2, 2, 6], dsp: [2, 2, 2, 6] },
      { name: 'PKG 1 · On Premise – On Lot · Sams Club', plan: [2, 2, 2, 6], dsp: [2, 2, 2, 6] },
      { name: 'PKG 1 · Neighborhoods – Loyalty · Sams Club', plan: [2, 2, 2, 6], dsp: [2, 2, 2, 6] },
      { name: 'PKG 1 · Custom Audience · Gen Z, Millennials & Gen X', plan: [2, 2, 2, 6], dsp: [2, 2, 2, 6] },
      { name: 'PKG 1 · 3P Audience · Dry Skin In-Market', plan: [2, 2, 2, 6], dsp: [2, 2, 2, 6] },
      { name: 'PKG 4 · Display - National', plan: [2, 2, 2, 6], dsp: [2, 0, 2, 4] },
      { name: 'PKG 7 · CRM Lists - Display Banners', plan: [2, 2, 2, 6], dsp: [2, 2, 2, 6] },
      { name: 'PKG 12 · Geofarm Conquest', plan: [2, 2, 2, 6], dsp: [1, 2, 2, 5] },
      { name: 'PKG 14 · Beauty Enthusiast 3P', plan: [2, 2, 2, 6], dsp: [2, 2, 2, 6] },
      { name: 'PKG 19 · Lookalike - Mobile Video', plan: [1, 1, 1, 3], dsp: [1, 1, 1, 3] },
      { name: 'PKG 22 · Retargeting - Mobile', plan: [2, 2, 2, 6], dsp: [2, 2, 2, 6] },
    ],
    total: { plan: [23, 23, 23, 69], dsp: [22, 21, 23, 66] },
  },
  locations: {
    badge: '3 mismatched',
    cols: [
      { key: 'name', label: 'Ad group' },
      { key: 'zips', label: 'ZIP codes', paired: true },
      { key: 'fil', label: 'Filters', paired: true },
      { key: 'miss', label: 'Missing in DSP' },
      { key: 'status', label: 'Status' },
    ],
    rows: [
      { name: 'PKG 1 · Location Audience – Loyalty · Sams Club', plan: [592, 592], dsp: [592, 592], miss: 'No' },
      { name: 'PKG 1 · On Premise – In Store · Sams Club', plan: [592, 592], dsp: [592, 592], miss: 'No' },
      { name: 'PKG 1 · Neighborhoods – Loyalty · Sams Club', plan: [592, 592], dsp: [592, 592], miss: 'No' },
      { name: 'PKG 2 · OLV - Premium Pre-roll', plan: [450, 450], dsp: [450, 450], miss: 'No' },
      { name: 'PKG 4 · Display - National', plan: [31200, 31200], dsp: [33145, 33145], miss: '48-state exclusion not applied — running everywhere' },
      { name: 'PKG 6 · L&L - Kingsport', plan: [12, 12], dsp: [12, 12], miss: 'No' },
      { name: 'PKG 7 · CRM Lists - Display Banners', plan: [2840, 2840], dsp: [2840, 2840], miss: 'No' },
      { name: 'PKG 12 · Geofarm Conquest', plan: [1240, 1240], dsp: [1188, 1188], miss: '52 ZIPs rejected by GroundTruth (low traffic)' },
      { name: 'PKG 15 · Retail Block - 2mi', plan: [820, 820], dsp: [820, 820], miss: 'No' },
      { name: 'PKG 18 · Event Geofence', plan: [48, 48], dsp: [42, 42], miss: '6 geofences below DSP minimum radius' },
      { name: 'PKG 21 · DMA Targeting', plan: [112, 112], dsp: [112, 112], miss: 'No' },
    ],
    total: { plan: [37990, 37990], dsp: [39820, 39820] },
  },
};
const QA_PAGE_SIZE = 10;

// Demo grid column ← [plan, DSP] pair field, with the label the annotation uses.
// Every paired field below that DISAGREES becomes a cell annotation, so the demo
// cannot claim `status: 'mismatch'` while its cells show no difference.
//
// These annotations are load-bearing, not decoration: demo mode opens with the
// issues-only filter engaged (gridOnlyMis defaults to demoMode) and that filter
// keeps rows by `annotations`. While these rows carried `annotations: {}` the
// filter dropped all twelve and the grid rendered "No rows match the current
// filter" — an empty demo, and four failing tests in this file.
const DEMO_PAIRED_COLS = [
  ['dailyCap', 'daily', 'Daily budget'],
  ['budget', 'total', 'Total budget'],
  ['audiences', 'audience', 'Audience'],
  ['geo', 'geo', 'Geography'],
  ['creative', 'creative', 'Creative'],
  ['bidFloor', 'bid', 'Bid'],
];

const DEMO_RECON_ROWS = [
  { id: 'pkg7-crm-disp', pkg: 'PKG 7', name: 'CRM Lists - Display Banners', tactic: 'DISP', daily: ['$420', '$380'], total: ['$12,600', '$11,400'], audience: ['Sams_CRM_Loyalty', 'Sams_CRM_Loyalty'], geo: ['National', 'National'], creative: ['display_300x250_v2', 'display_300x250_v2'], bid: ['$6.50 CPM', '$6.50 CPM'], status: 'mismatch' },
  { id: 'pkg12-geo-conq', pkg: 'PKG 12', name: 'Geofarm Conquest', tactic: 'DISP', daily: ['$420', '$380'], total: ['$12,600', '$11,400'], audience: ['Conquest_Big5', 'Conquest_Big5'], geo: ['Top 25 DMAs', 'Top 25 DMAs'], creative: ['display_300x250_v2', 'display_300x250_v2'], bid: ['$7.20 CPM', '$7.20 CPM'], status: 'mismatch' },
  { id: 'pkg19-lal-vid', pkg: 'PKG 19', name: 'Lookalike - Mobile Video', tactic: 'OLV', daily: ['$180', '$180'], total: ['$5,400', '$5,400'], audience: ['Sams_LAL_2pct_Mar26', 'Sams_LAL_5pct_Feb26'], geo: ['National', 'National'], creative: ['video_15s_brand', 'video_15s_brand'], bid: ['$12.00 CPM', '$12.00 CPM'], status: 'mismatch' },
  { id: 'pkg4-disp-nat', pkg: 'PKG 4', name: 'Display - National', tactic: 'DISP', daily: ['$260', '$260'], total: ['$7,800', '$7,800'], audience: ['Open', 'Open'], geo: ['48 states', '50 states + DC + PR'], creative: ['display_728x90_v1', 'display_728x90_v1'], bid: ['$4.80 CPM', '$4.80 CPM'], status: 'mismatch' },
  { id: 'pkg1-disp-cnt', pkg: 'PKG 1', name: 'Display - Contextual', tactic: 'DISP', daily: ['$180', '$180'], total: ['$5,400', '$5,400'], audience: ['Open', 'Open'], geo: ['National', 'National'], creative: ['display_300x600', 'display_300x600'], bid: ['$5.20 CPM', '$5.20 CPM'], status: 'match' },
  { id: 'pkg2-olv-pre', pkg: 'PKG 2', name: 'OLV - Premium Pre-roll', tactic: 'OLV', daily: ['$280', '$280'], total: ['$8,400', '$8,400'], audience: ['1P_Loyalty', '1P_Loyalty'], geo: ['National', 'National'], creative: ['video_15s_brand', 'video_15s_brand'], bid: ['$14.00 CPM', '$14.00 CPM'], status: 'match' },
  { id: 'pkg3-prox-aud', pkg: 'PKG 3', name: 'Proximity - Audience Extension', tactic: 'PROX', daily: ['$140', '$140'], total: ['$4,200', '$4,200'], audience: ['Sams_Visitors', 'Sams_Visitors'], geo: ['2mi radial', '2mi radial'], creative: ['display_300x250_v2', 'display_300x250_v2'], bid: ['$8.00 CPM', '$8.00 CPM'], status: 'match' },
  { id: 'pkg5-conq-q', pkg: 'PKG 5', name: 'Competitor Conquest', tactic: 'CONQ', daily: ['$110', '$110'], total: ['$3,300', '$3,300'], audience: ['BJs_Costco_visitors', 'BJs_Costco_visitors'], geo: ['National', 'National'], creative: ['display_300x600', 'display_300x600'], bid: ['$6.80 CPM', '$6.80 CPM'], status: 'match' },
  { id: 'pkg6-ll-king', pkg: 'PKG 6', name: 'L&L - Kingsport', tactic: 'L&L', daily: ['$12', '$12'], total: ['$360', '$360'], audience: ['Open', 'Open'], geo: ['2mi geofence', '2mi geofence'], creative: ['display_300x250_v2', 'display_300x250_v2'], bid: ['$5.00 CPM', '$5.00 CPM'], status: 'match' },
  { id: 'pkg8-olv-ctv', pkg: 'PKG 8', name: 'OLV - Connected TV', tactic: 'OLV', daily: ['$320', '$320'], total: ['$9,600', '$9,600'], audience: ['1P_Loyalty', '1P_Loyalty'], geo: ['National', 'National'], creative: ['video_30s_brand', 'video_30s_brand'], bid: ['$26.00 CPM', '$26.00 CPM'], status: 'match' },
  { id: 'pkg9-prox-evt', pkg: 'PKG 9', name: 'Proximity - Event', tactic: 'PROX', daily: ['$90', '$90'], total: ['$2,700', '$2,700'], audience: ['Open', 'Open'], geo: ['Event 1mi', 'Event 1mi'], creative: ['display_300x250_v2', 'display_300x250_v2'], bid: ['$9.00 CPM', '$9.00 CPM'], status: 'match' },
  { id: 'pkg10-disp-rt', pkg: 'PKG 10', name: 'Display - Retargeting', tactic: 'DISP', daily: ['$150', '$150'], total: ['$4,500', '$4,500'], audience: ['Site_Visitors_30d', 'Site_Visitors_30d'], geo: ['National', 'National'], creative: ['display_160x600', 'display_160x600'], bid: ['$4.20 CPM', '$4.20 CPM'], status: 'match' },
].map((r) => ({
  ...r,
  // Synthetic review row so the demo grid opens the same cell detail panel
  // the real data does (plan side of each pair). Demo edits are never
  // persisted — saveGridEdits short-circuits with a toast.
  srcRow: {
    rowId: r.id,
    name: r.name,
    package: r.pkg,
    tactic: r.tactic,
    channel: 'GroundTruth',
    dailyCap: r.daily[0],
    budget: r.total[0],
    audiences: r.audience[0],
    audienceList: r.audience[0],
    geo: r.geo[0],
    geoIncludeList: r.geo[0],
    creative: r.creative[0],
    bidFloor: r.bid[0],
    annotations: DEMO_PAIRED_COLS.reduce((acc, [col, src, title]) => {
      const [plan, dsp] = r[src] || [];
      if (plan !== undefined && dsp !== undefined && plan !== dsp) {
        acc[col] = {
          type: 'm',
          msg: `${title} — DSP reports ${dsp}, the plan says ${plan}.`,
          category: 'DSP-side change',
          raw: plan,
        };
      }
      return acc;
    }, {}),
  },
}));

// ── Real-data helpers ────────────────────────────────────────────────
const fmtMoney = (n, dp = 0) => (n == null || Number.isNaN(Number(n))
  ? '—'
  : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp }));
const fmtInt = (n) => (n == null || Number.isNaN(Number(n)) ? '—' : Math.round(Number(n)).toLocaleString('en-US'));

const FMT_TAG_CLS = {
  DISP: 'cmp-tag-disp', MOB: 'cmp-tag-disp', OLV: 'cmp-tag-olv', VID: 'cmp-tag-olv',
  OTT: 'cmp-tag-olv', CTV: 'cmp-tag-olv', PROX: 'cmp-tag-prox', GEO: 'cmp-tag-prox',
  CONQ: 'cmp-tag-conq', AUD: 'cmp-tag-conq', 'L&L': 'cmp-tag-ll',
  // Google Ads channels (see gads/gadsConstants.js#GADS_FMT).
  SEM: 'cmp-tag-conq', PMAX: 'cmp-tag-prox', DGEN: 'cmp-tag-olv',
};

const PLATFORM_LABELS = {
  ttd: 'TTD', dv360: 'DV360', yahoo: 'Yahoo', gt: 'GroundTruth', groundtruth: 'GroundTruth',
};
// Google Ads campaigns aren't a "DSP" — label them by channel, resolving every
// destination / platform spelling through the shared alias table. Facebook is
// the same case: a bought-media platform, not a DSP.
const platformLabel = (p) => {
  const key = String(p || '').toLowerCase();
  if (PLATFORM_LABELS[key]) return PLATFORM_LABELS[key];
  const sub = gadsSubtype(key);
  if (sub) return GADS_SUBTYPE_LABELS[sub];
  if (isFbPlatform(key)) return FB_PLATFORM_LABEL;
  return p || 'DSP';
};

// Campaign budget for the header stat. Impression-only plans (no currency
// budget) carry totalBudget=0 with the KPI-CPM estimate in estimatedBudget
// (DV360) — and the Launch board's launch-list metric applies the same
// estimate server-side. Resolve in that order so this page always agrees
// with the board; estimates render with the board's `~` prefix.
const resolveCampaignBudget = (summary, campaignRow, rows = []) => {
  if (Number(summary?.totalBudget) > 0) {
    return { text: fmtMoney(summary.totalBudget), sub: 'From campaign plan' };
  }
  if (Number(summary?.estimatedBudget) > 0) {
    // DV360 estimates from impressions × KPI CPM; Google Ads from daily budget
    // × flight days. The summarizer says which, so the caption stays truthful.
    return {
      text: `~${fmtMoney(summary.estimatedBudget)}`,
      sub: summary.estimatedBudgetBasis || 'Estimated from impressions × CPM',
    };
  }
  if (campaignRow?.budget != null) {
    return {
      text: `${campaignRow.budgetEstimated ? '~' : ''}${fmtMoney(campaignRow.budget)}`,
      sub: campaignRow.budgetEstimated ? 'Estimated from impressions × CPM' : 'From campaign plan',
    };
  }
  // Last resort: sum the per-row impression×CPM estimates (TTD impression-
  // budgeted plans have neither a summary estimate nor a board metric).
  const rowSum = rows.reduce((a, r) => a + rowBudgetValue(r).amount, 0);
  if (rowSum > 0) {
    return { text: `~${fmtMoney(rowSum)}`, sub: 'Estimated from impressions × CPM' };
  }
  return { text: '—', sub: 'From campaign plan' };
};

// Per-row budget with the same impression×CPM fallback, for the breakdown
// tables and the grid (row builders leave budget null on impression-only
// plans, which would otherwise sum to $0).
const rowBudgetValue = (r) => {
  const b = Number(r.budget);
  // `budgetEstimated` is set by builders whose row budget is itself derived
  // (Google Ads: daily budget × flight days) so the `~` carries through to the
  // breakdown tables instead of only appearing in the header.
  if (Number.isFinite(b) && b > 0) return { amount: b, estimated: Boolean(r.budgetEstimated) };
  const imps = Number(r.imps) || 0;
  const cpm = Number(r.cpm) || 0;
  if (imps > 0 && cpm > 0) return { amount: (imps / 1000) * cpm, estimated: true };
  return { amount: 0, estimated: false };
};
const fmtBudgetCell = ({ amount, estimated }) => (amount > 0 ? `${estimated ? '~' : ''}${fmtMoney(amount)}` : '—');

const cellText = (v) => {
  const s = v == null ? '' : String(v).trim();
  if (!s || s === '—') return '—';
  return s.length > 46 ? `${s.slice(0, 43)}…` : s;
};
const leadingCount = (s) => {
  const m = /^(\d+)/.exec(String(s || ''));
  return m ? Number(m[1]) : 0;
};
const listCount = (s) => (!s || s === '—' ? 0 : String(s).split(/\s*[·,]\s*/).filter(Boolean).length);

// One CLW review row (any platform) → the shared grid-row shape. DSP side
// mirrors the plan; when a reconciliation ad-group diff is provided, each
// mismatched field's DSP side shows the live value instead.
const planGridRow = (r, agDiff) => {
  const pair = (v) => [cellText(v), cellText(v)];
  const f = agDiff?.fields || {};
  const over = (cellPair, fd, fmt) => (
    fd && fd.compared && !fd.match ? [cellPair[0], cellText(fmt(fd.dsp))] : cellPair
  );
  const budget = fmtBudgetCell(rowBudgetValue(r));
  const cpm = r.cpm != null ? `${fmtMoney(r.cpm, 2)} CPM` : (r.bid_price ? cellText(r.bid_price) : '—');
  const missing = agDiff?.liveStatus === 'missing';
  return {
    id: r.rowId,
    // The full CLW review row, kept so a cell click can hand CellDetailPanel
    // every field (audience/geo lists, annotations, rowId) — the flattened
    // pairs below are display-only.
    srcRow: r,
    pkg: cellText(r.market && r.market !== '—' ? r.market : r.package),
    name: cellText(missing ? `${r.name} — missing in DSP` : r.name),
    tactic: cellText(r.tactic && r.tactic !== '—' ? r.tactic : r.fmt),
    // `dailyLabel` is set by platforms whose daily figure is currency rather
    // than DV360's impression cap (Google Ads) — it wins so the cell doesn't
    // render "$10.00/day" as the integer 10.
    daily: over(pair(r.dailyLabel && r.dailyLabel !== '—'
      ? r.dailyLabel
      : (r.dailyCap != null && r.dailyCap !== '—' ? fmtInt(r.dailyCap) : (r.pacing || '—'))), f.pacing, (v) => v),
    total: pair(budget),
    audience: pair(r.audiences || r.targeting_identifier),
    geo: pair(r.geo || r.location_targeting),
    creative: over(pair(r.creative || r.creative_sizes), f.creativeCount, (v) => `${v} asset${Number(v) === 1 ? '' : 's'}`),
    bid: over(pair(r.bidStrategy && r.bidStrategy !== '—' ? r.bidStrategy : cpm), f.baseBid, (v) => `${fmtMoney(v, 2)} CPM`),
    status: agDiff && agDiff.status === 'mismatch' ? 'mismatch' : 'match',
  };
};

// Which builder family a launcher result belongs to:
// 'ttd' | 'dv360' | 'gads' | 'fb'. Read from the payload's own platform field,
// with a shape check for Google Ads and Facebook so a plan whose platform got
// dropped upstream still resolves — Facebook depends on that check, because the
// FB pipeline never stamps `platform` on parsed_response. DV360 stays the
// default, which is what it has always been for an unrecognised result.
//
// Mirrors CampaignLaunchWorkflow.js#launcherFamily; keep the two in step.
const resolveLrPlatform = (lr) => {
  if (isYahooResult(lr)) return 'yahoo';
  const platform = String(lr?.platform || lr?.Platform || '').toLowerCase();
  if (platform === 'ttd' || platform === 'thetradedesk') return 'ttd';
  if (gadsSubtype(platform)) return 'gads';
  if (isFbPlatform(platform)) return 'fb';
  if (platform === 'dv360') return 'dv360';
  // Unknown or absent platform: decide on payload shape, still defaulting to
  // DV360 so an unrecognised result behaves exactly as it did before.
  if (isGadsLauncherResult(lr)) return 'gads';
  if (isFbLauncherResult(lr)) return 'fb';
  return 'dv360';
};

// Summary + rows for a launcher result, dispatched by builder family. One
// place so the initial load, the post-save refresh and the audience-refresh
// overlay can never drift into using different builders for the same plan.
const rebuildPlan = (lr, lrPlatform) => {
  if (lrPlatform === 'yahoo') return { summary: summarizeYahooResult(lr), rows: buildYahooReviewRows(lr) };
  if (lrPlatform === 'gads') {
    return { summary: summarizeGadsResult(lr), rows: buildGadsReviewRows(lr) };
  }
  if (lrPlatform === 'fb') {
    return { summary: summarizeFbResult(lr), rows: buildFbReviewRows(lr) };
  }
  if (lrPlatform === 'ttd') {
    return { summary: summarizeTtdResult(lr), rows: buildTtdReviewRows(lr) };
  }
  return { summary: summarizeDv360Result(lr), rows: buildDv360ReviewRows(lr) };
};

// Account feature flag gating push-to-DSP — the only reconciliation action
// that writes to the live platform. Mirrors
// apps/app-backend/utils/pushToDspFlag.js; keep the key in sync.
const PUSH_TO_DSP_KEY = 'enable-v3-ui-launch-push-to-dsp';
// Only TTD and DV360 have a reconcile-push handler. Google Ads has none, so
// gads campaigns get no auto-sync on open and no push affordance — the same
// treatment GroundTruth gets.
const pushPlatformKey = (destination) => {
  const d = String(destination || '').toLowerCase();
  if (d === 'ttd' || d === 'thetradedesk') return 'ttd';
  if (d === 'dv360') return 'dv360';
  return null;
};

// Adapt a server-side reconciliation discrepancy to the UI row shape used by
// the (demo-originated) discrepancy renderer.
const serverDisc = (d) => ({
  id: d.id,
  sev: d.severity === 'high' ? 'high' : (d.severity === 'med' ? 'med' : 'low'),
  text: d.title,
  diff: d.diff,
  plan: d.plan || [],
  dsp: d.dsp || [],
  explain: d.likelyCause || 'Value differs between the plan and the DSP.',
  push: d.canPush ? { label: `↑ Push plan value to DSP (${d.diff?.plan ?? 'plan'})`, fix: d.pushFix } : null,
  accept: d.canAccept ? { label: `↓ Accept DSP value (${d.diff?.dsp ?? 'DSP'}) — update plan`, fix: d.acceptFix } : null,
});

// A DSP sync older than this is treated as stale: the header flags it and the
// page kicks off a refresh on open.
const STALE_SYNC_MS = 24 * 60 * 60 * 1000;

// Discrepancy `field` → the CLW grid column it belongs to, so every entry in
// the Discrepancies tab lands on the cell a reviewer would look at. Covers all
// three field sets the reconciler emits (AG_FIELDS / CAMPAIGN_FIELDS /
// IO_FIELDS) plus the 'missing' / 'extra' sentinels it smuggles into `field`
// for structural problems, which belong on the name cell.
const DISC_FIELD_TO_COL = {
  // ad-group level
  baseBid: 'cpm',
  maxBid: 'bidFloor',
  pacing: 'pacing',
  freqCap: 'freqCap',
  creativeCount: 'creative',
  audienceCount: 'audiences',
  thirdPartySegmentCount: 'audiences',
  geoCount: 'geo',
  geoExclusionCount: 'geo',
  // campaign / IO level
  budget: 'budget',
  startDate: 'start',
  endDate: 'end',
  // structural
  missing: 'name',
  extra: 'name',
};

const timeAgo = (iso) => {
  if (!iso) return null;
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  return hrs < 24 ? `${hrs} hr ago` : `${Math.round(hrs / 24)} d ago`;
};

// Group review rows by format code → tactic-level summary rows. When a
// reconciliation has run, `mismatchNames` (Set of ad-group names with live
// diffs) drives the status tags; otherwise they read "Sync pending".
const buildTacticRows = (rows, mismatchNames = null) => {
  const groups = new Map();
  for (const r of rows) {
    const key = (r.fmt && r.fmt !== '—' ? String(r.fmt) : 'OTHER').toUpperCase();
    const g = groups.get(key) || { ag: 0, budget: 0, budgetEst: false, imps: 0, mis: 0 };
    g.ag += 1;
    const b = rowBudgetValue(r);
    g.budget += b.amount;
    g.budgetEst = g.budgetEst || b.estimated;
    g.imps += Number(r.imps) || 0;
    if (mismatchNames && mismatchNames.has(String(r.name || '').trim().toLowerCase())) g.mis += 1;
    groups.set(key, g);
  }
  const groupBudget = (g) => fmtBudgetCell({ amount: g.budget, estimated: g.budgetEst });
  const statusOf = (mis) => {
    if (!mismatchNames) return { status: 'Sync pending', statusStyle: TAG_STYLES.pending, mismatch: false };
    return mis > 0
      ? { status: `${mis} mismatched`, statusStyle: TAG_STYLES.mis2, mismatch: true }
      : { status: 'All match', statusStyle: TAG_STYLES.match, mismatch: false };
  };
  const tacticRows = [...groups.entries()].map(([tag, g]) => ({
    tag,
    tagCls: FMT_TAG_CLS[tag] || 'cmp-tag-disp',
    name: tag === 'OTHER' ? 'Other' : tag,
    ag: fmtInt(g.ag),
    planB: groupBudget(g), dspB: groupBudget(g), dB: '$0', dBCls: 'ok',
    planI: g.imps ? fmtInt(g.imps) : '—', dspI: g.imps ? fmtInt(g.imps) : '—', dI: '0', dICls: 'ok',
    ...statusOf(g.mis),
  }));
  const t = [...groups.values()].reduce(
    (a, g) => ({ ag: a.ag + g.ag, budget: a.budget + g.budget, budgetEst: a.budgetEst || g.budgetEst, imps: a.imps + g.imps, mis: a.mis + g.mis }),
    { ag: 0, budget: 0, budgetEst: false, imps: 0, mis: 0 },
  );
  const totalBudget = fmtBudgetCell({ amount: t.budget, estimated: t.budgetEst });
  const tacticTotal = {
    ag: fmtInt(t.ag),
    planB: totalBudget, dspB: totalBudget, dB: '$0', dBCls: 'ok',
    planI: t.imps ? fmtInt(t.imps) : '—', dspI: t.imps ? fmtInt(t.imps) : '—', dI: '0', dICls: 'ok',
    status: mismatchNames ? (t.mis > 0 ? `${t.mis} mismatched` : 'All match') : 'Sync pending',
    statusStyle: mismatchNames ? (t.mis > 0 ? TAG_STYLES.mis2 : TAG_STYLES.match) : TAG_STYLES.pending,
  };
  return { tacticRows, tacticTotal };
};

// Aggregate review rows → targeting & setup summary. Plan side is always
// real; when a reconciliation has run (`diffByName`: normalized name →
// ad-group diff), the DSP side shows live aggregates for the fields the
// sync compares (pacing, frequency caps, creative counts) with
// Match/Mismatch deltas. Fields the sync doesn't read yet (audience/geo
// attachments) read "Not compared"; before any sync everything reads
// "Sync pending".
const buildSetupRows = (rows, diffByName = null) => {
  const synced = Boolean(diffByName);
  const norm = (s) => String(s || '').trim().toLowerCase();
  const fdOf = (r, key) => {
    const fd = diffByName?.get(norm(r.name))?.fields?.[key];
    return fd && fd.compared ? fd : null;
  };

  // Audience / geo / segment attachments: compared when the sync fetched
  // them (DV360 assigned targeting; TTD audience→data-group resolution and
  // geo bidlists); otherwise the plan-side count mirrors across.
  let withAud = 0; let liveAud = 0; let audCompared = false; let audMis = false;
  let withGeo = 0; let liveGeo = 0; let geoCompared = false; let geoMis = false;
  let planKwSegs = 0; let liveKwSegs = 0; let kwCompared = false; let kwMis = false;
  let withGeoExc = 0; let liveGeoExc = 0; let geoExcCompared = false; let geoExcMis = false;
  for (const r of rows) {
    const planHasAud = (r.audiences || r.targeting_identifier || '—') !== '—';
    if (planHasAud) withAud += 1;
    const a = fdOf(r, 'audienceCount') || fdOf(r, 'thirdPartySegmentCount');
    if (a) { audCompared = true; if (Number(a.dsp) > 0) liveAud += 1; if (!a.match) audMis = true; } else if (planHasAud) liveAud += 1;

    const planHasGeo = (r.geo || r.location_targeting || '—') !== '—';
    if (planHasGeo) withGeo += 1;
    const g = fdOf(r, 'geoCount');
    if (g) { geoCompared = true; if (Number(g.dsp) > 0) liveGeo += 1; if (!g.match) geoMis = true; } else if (planHasGeo) liveGeo += 1;

    const s = fdOf(r, 'thirdPartySegmentCount');
    if (s) { kwCompared = true; planKwSegs += Number(s.plan) || 0; liveKwSegs += Number(s.dsp) || 0; if (!s.match) kwMis = true; }

    const planHasGeoExc = (r.geoExcludeList || '—') !== '—';
    if (planHasGeoExc) withGeoExc += 1;
    const ge = fdOf(r, 'geoExclusionCount');
    if (ge) { geoExcCompared = true; if (Number(ge.dsp) > 0) liveGeoExc += 1; if (!ge.match) geoExcMis = true; } else if (planHasGeoExc) liveGeoExc += 1;
  }
  // Keyword/segment fallback when not compared: the plan's raw search-term
  // count (what the row showed before segment resolution existed).
  const kw = rows.reduce((a, r) => a + (Number(r.keywordCount) || 0), 0);

  // Live-aware aggregates for the compared fields.
  let planCreatives = 0; let liveCreatives = 0; let creativesCompared = false; let creativesMis = false;
  let planFreq = 0; let liveFreq = 0; let freqCompared = false; let freqMis = false;
  const planPacings = new Set(); const livePacings = new Set(); let pacingCompared = false; let pacingMis = false;
  for (const r of rows) {
    const planC = leadingCount(r.creative) + listCount(r.creative_sizes);
    planCreatives += planC;
    const c = fdOf(r, 'creativeCount');
    if (c) { creativesCompared = true; liveCreatives += Number(c.dsp) || 0; if (!c.match) creativesMis = true; } else liveCreatives += planC;

    const planHasFreq = (r.freqCap || '—') !== '—';
    if (planHasFreq) planFreq += 1;
    const f = fdOf(r, 'freqCap');
    if (f) { freqCompared = true; if (f.dsp) liveFreq += 1; if (!f.match) freqMis = true; } else if (planHasFreq) liveFreq += 1;

    if (r.pacing && r.pacing !== '—') planPacings.add(r.pacing);
    const p = fdOf(r, 'pacing');
    if (p) { pacingCompared = true; if (p.dsp) livePacings.add(String(p.dsp)); if (!p.match) pacingMis = true; } else if (r.pacing && r.pacing !== '—') livePacings.add(r.pacing);
  }

  // Delta cell states: pending (no sync yet), notCompared (sync ran but the
  // field isn't read from the DSP), match / mismatch.
  const pending = { delta: 'Sync pending', ok: true, cls: 'neutral' };
  const notCompared = { delta: 'Not compared', ok: true, cls: 'neutral' };
  const compared = (mis) => (mis ? { delta: 'Mismatch', ok: false } : { delta: 'Match', ok: true });
  const stateOf = (isCompared, mis) => (!synced ? pending : (isCompared ? compared(mis) : notCompared));

  const mirror = (plan, state) => ({ plan, dsp: plan, ...state });
  const livePair = (plan, dsp, state) => ({ plan, dsp, ...state });

  return [
    { section: 'Audiences' },
    { name: 'Ad groups with audiences attached', ...livePair(fmtInt(withAud), fmtInt(liveAud), stateOf(audCompared, audMis)) },
    { name: '3rd-party segments', ...(kwCompared
      ? livePair(fmtInt(planKwSegs), fmtInt(liveKwSegs), stateOf(true, kwMis))
      : mirror(fmtInt(kw), stateOf(false))) },
    { section: 'Geo' },
    { name: 'Ad groups with geo targeting', ...livePair(fmtInt(withGeo), fmtInt(liveGeo), stateOf(geoCompared, geoMis)) },
    { name: 'Ad groups with geo exclusions', ...livePair(fmtInt(withGeoExc), fmtInt(liveGeoExc), stateOf(geoExcCompared, geoExcMis)) },
    { section: 'Creative' },
    { name: 'Creative assets trafficked', ...livePair(fmtInt(planCreatives), fmtInt(liveCreatives), stateOf(creativesCompared, creativesMis)) },
    { section: 'Bid & pacing' },
    { name: 'Pacing', ...livePair(
      planPacings.size ? [...planPacings].join(' · ') : '—',
      livePacings.size ? [...livePacings].join(' · ') : '—',
      stateOf(pacingCompared, pacingMis),
    ) },
    { name: 'Ad groups with frequency caps', ...livePair(fmtInt(planFreq), fmtInt(liveFreq), stateOf(freqCompared, freqMis)) },
  ];
};

// Per-ad-group QA reports from review rows. Plan side is always real; when a
// reconciliation has run (`diffByName`), the DSP column shows live values for
// the compared field and rows carry a `compared` flag so the status cell can
// distinguish Match/Mismatch from Not compared.
const buildQaReports = (rows, diffByName = null) => {
  const isGt = rows.some((r) => r.channel === 'GT');
  const norm = (s) => String(s || '').trim().toLowerCase();
  const fdOf = (r, key) => {
    const fd = diffByName?.get(norm(r.name))?.fields?.[key];
    return fd && fd.compared ? fd : null;
  };
  const creativeCount = (r) => (isGt
    ? listCount(r.creative_sizes) || Number(r.creative_versions) || 0
    : leadingCount(r.creative));
  const geoCount = (r) => (isGt
    ? ((r.location_targeting && r.location_targeting !== '—') ? 1 : 0)
    : listCount(r.geoIncludeList));
  const rowsOf = (fn, fieldKey) => rows.map((r) => {
    const fd = fdOf(r, fieldKey);
    if (fd) {
      return { name: r.name, plan: [Number(fd.plan) || 0], dsp: [Number(fd.dsp) || 0], compared: true };
    }
    const n = fn(r);
    return { name: r.name, plan: [n], dsp: [n], compared: false };
  });
  const totalOf = (list) => ({
    plan: [list.reduce((a, r) => a + r.plan[0], 0)],
    dsp: [list.reduce((a, r) => a + r.dsp[0], 0)],
  });
  const badgeOf = (list) => {
    if (!diffByName) return 'DSP sync pending';
    const mis = list.filter((r) => r.plan[0] !== r.dsp[0]).length;
    return mis > 0 ? `${mis} mismatched` : 'All match';
  };
  const creativesRows = rowsOf(creativeCount, 'creativeCount');
  const locationRows = rowsOf(geoCount, 'geoCount');
  return {
    creatives: {
      badge: badgeOf(creativesRows),
      cols: [
        { key: 'name', label: 'Ad group' },
        { key: 'assets', label: isGt ? 'Creative sizes' : 'Creative assets', paired: true },
        { key: 'status', label: 'Status' },
      ],
      rows: creativesRows,
      total: totalOf(creativesRows),
    },
    locations: {
      badge: badgeOf(locationRows),
      cols: [
        { key: 'name', label: 'Ad group' },
        { key: 'geos', label: isGt ? 'Location targets' : 'Geo targets', paired: true },
        { key: 'status', label: 'Status' },
      ],
      rows: locationRows,
      total: totalOf(locationRows),
    },
  };
};

// ── Plan-review view builders (plan-stage campaigns) ────────────────

// Group review rows by format code → format breakdown table rows.
const buildFormatBreakdown = (rows) => {
  const groups = new Map();
  for (const r of rows) {
    const key = (r.fmt && r.fmt !== '—' ? String(r.fmt) : 'OTHER').toUpperCase();
    const g = groups.get(key) || { ag: 0, pkgs: new Set(), budget: 0, budgetEst: false, imps: 0, starts: [], ends: [] };
    g.ag += 1;
    const pkg = r.market && r.market !== '—' ? r.market : r.package;
    if (pkg && pkg !== '—') g.pkgs.add(String(pkg));
    const b = rowBudgetValue(r);
    g.budget += b.amount;
    g.budgetEst = g.budgetEst || b.estimated;
    g.imps += Number(r.imps) || 0;
    const start = r.start || r.start_date;
    const end = r.end || r.end_date;
    if (start && start !== '—') g.starts.push(String(start));
    if (end && end !== '—') g.ends.push(String(end));
    groups.set(key, g);
  }
  return [...groups.entries()].map(([tag, g]) => ({
    tag,
    tagCls: FMT_TAG_CLS[tag] || 'cmp-tag-disp',
    ag: fmtInt(g.ag),
    pkgs: g.pkgs.size ? fmtInt(g.pkgs.size) : '—',
    budget: fmtBudgetCell({ amount: g.budget, estimated: g.budgetEst }),
    cpm: g.budget && g.imps ? fmtMoney((g.budget / g.imps) * 1000, 2) : '—',
    imps: g.imps ? fmtInt(g.imps) : '—',
    flight: g.starts.length && g.ends.length
      ? `${g.starts.sort()[0]} – ${g.ends.sort().slice(-1)[0]}`
      : '—',
  }));
};

// GT ad-group names lead with "PKG N_…"; the design cites those numbers in the
// flagged-item summary ("PKGs 63 & 64 · …"). Returns null for other platforms,
// whose names carry no package number.
const pkgNumberOf = (name) => {
  const m = String(name || '').match(/PKG\s*(\d+)/i);
  return m ? m[1] : null;
};

// Flatten row annotations (CLW's validation issues: templateErrors/warnings
// for TTD/DV360, ctsReview errors/warnings for GT) → flagged-item rows.
//
// The same validator finding usually repeats across every ad group it touches
// (one CRM audience gap becomes one annotation per package). The design shows
// those collapsed into a single row citing all affected packages, so identical
// (field, type, message) findings are grouped here and the affected ad groups
// travel with the item for the detail view and the Plan Grid jump.
const buildFlaggedIssues = (rows) => {
  const byKey = new Map();
  for (const r of rows) {
    for (const [field, ann] of Object.entries(r.annotations || {})) {
      for (const a of [ann, ...(ann?.extra || [])]) {
        if (!a || a.type === 'i') continue;
        const sev = a.type === 'm' ? 'missing' : 'question';
        const msg = a.msg || 'Needs input';
        const key = `${sev}|${field}|${msg}`;
        if (!byKey.has(key)) {
          byKey.set(key, {
            id: key,
            sev,
            msg,
            field,
            // `raw` is the plan's original input for this cell (DV360's row
            // builder captures it); `path` links agent-authored findings back
            // to the suggestion that proposed a fix.
            raw: a.raw || null,
            path: a.path || null,
            rows: [],
            rowIds: [],
            pkgs: [],
          });
        }
        const item = byKey.get(key);
        if (!item.rows.includes(r.name)) {
          item.rows.push(r.name);
          item.rowIds.push(r.rowId);
          const pkg = pkgNumberOf(r.name);
          if (pkg && !item.pkgs.includes(pkg)) item.pkgs.push(pkg);
        }
      }
    }
  }
  // `row` is kept as the first affected ad group so existing single-row
  // consumers keep working.
  return [...byKey.values()].map((i) => ({ ...i, row: i.rows[0] }));
};

// "PKGs 63 & 64 · CRM Lists - Mobile Banners / CRM Lists - Mobile Video"
const flaggedScopeLabel = (item) => {
  const parts = [];
  if (item.pkgs.length) {
    const list = item.pkgs.length > 2
      ? `${item.pkgs.slice(0, -1).join(', ')} & ${item.pkgs[item.pkgs.length - 1]}`
      : item.pkgs.join(' & ');
    parts.push(`${item.pkgs.length === 1 ? 'PKG' : 'PKGs'} ${list}`);
  }
  const names = item.rows.slice(0, 3).join(' / ');
  parts.push(item.rows.length > 3 ? `${names} +${item.rows.length - 3} more` : names);
  return parts.join(' · ');
};

// One plan-QA table row per ad group: creative/geo counts + issue count.
const buildPlanQaRows = (rows) => {
  const isGt = rows.some((r) => r.channel === 'GT');
  return rows.map((r) => ({
    name: r.name,
    creatives: isGt ? (listCount(r.creative_sizes) || Number(r.creative_versions) || 0) : leadingCount(r.creative),
    geos: isGt ? ((r.location_targeting && r.location_targeting !== '—') ? 1 : 0) : listCount(r.geoIncludeList),
    issues: Object.keys(r.annotations || {}).length,
  }));
};

const LaunchCampaignDetails = () => {
  const navigate = useNavigate();
  const { getAccountIdToUse, currentAccountName } = useAccount();
  const accountId = getAccountIdToUse();
  const [user] = useState(() => getUserData());
  const userId = user?.userId || user?.id;
  const { getSetting, settings: accountSettings } = useAccountSettings(accountId, user);

  const { campaignId, demoMode } = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return { campaignId: params.get('campaign'), demoMode: params.get('frommock') === 'demo' };
    } catch (e) { return { campaignId: null, demoMode: false }; }
  }, []);

  const [campaign, setCampaign] = useState(null); // {id,name,destination,stage} from /:id/stage
  const [campaignLoading, setCampaignLoading] = useState(!demoMode);
  const [plan, setPlan] = useState(null); // { summary, rows, alreadyLaunched } from planning-review
  const [planLoading, setPlanLoading] = useState(!demoMode);
  const [tab, setTab] = useState('qa'); // qa | disc | grid
  const [openDiscs, setOpenDiscs] = useState(() => new Set());
  const [resolved, setResolved] = useState({}); // id -> 'push' | 'accept'
  const [collapsedCards, setCollapsedCards] = useState(() => new Set());
  const [gridOnlyMis, setGridOnlyMis] = useState(demoMode);
  // Controlled view state for the shared CLW ReviewGrid (column-group tab +
  // its own search box). Held here rather than inside the grid so the tab
  // survives switching away and back.
  const [gridGroup, setGridGroup] = useState('overview');
  // Breakdown tab state — the pivot switcher (package / tactic / format /
  // month) and, for GT, which QA pivot is showing.
  const [breakdownView, setBreakdownView] = useState('package');
  const [qaPivotKey, setQaPivotKey] = useState(null);
  const [gridQuery, setGridQuery] = useState('');
  const [qaReport, setQaReport] = useState('creatives');
  const [qaOnlyMis, setQaOnlyMis] = useState(false);
  const [qaPage, setQaPage] = useState(0);
  const [toastMsg, setToastMsg] = useState(null);
  // Save-time notices. A toast is the wrong shape for these: they arrive several
  // at a time, each a full sentence about a different campaign, and the old code
  // put only the first in the toast with "(+2 more)" standing in for the rest —
  // the other two were unreadable anywhere. PlanNotices renders one row each,
  // above the grid, until dismissed.
  const [planNotices, setPlanNotices] = useState(null);
  const toastTimer = useRef(null);
  const [recon, setRecon] = useState(null); // GET /reconciliation response
  const [reconSyncing, setReconSyncing] = useState(false);
  // False until we've either loaded the reconciliation state or established
  // that we never will. Without it the header asserts "Never refreshed"
  // during the fetch — a claim we can't make yet — and then flips to
  // "Refreshing…", which reads as a flicker.
  const [reconKnown, setReconKnown] = useState(false);
  const reconPollTimer = useRef(null);
  const autoSyncedRef = useRef(null); // campaignId we've already auto-refreshed
  const [signoff, setSignoff] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  // Sidebar mode: 'bulk' (the default — change dates/budgets/geo across many
  // rows) or 'qa' (the MMGY reviewer agent, where one is wired for this plan).
  const [agentMode, setAgentMode] = useState('bulk');
  // The reviewer on this page is the MMGY agent, which reasons about the MMGY
  // pipeline — so it may only run for MMGY accounts. Same derivation as CLW's
  // `isMmgy` and the legacy CampaignCreation check: exactly `mmgy`, or an
  // `mmgy-` sub-account. The backend enforces this at session create (403); this
  // is what stops the sidebar offering a tab that would fail.
  const isMmgyAccount = useMemo(() => {
    const n = (currentAccountName || '').toLowerCase();
    return n === 'mmgy' || n.startsWith('mmgy-');
  }, [currentAccountName]);
  const [aiSuggestionStatus, setAiSuggestionStatus] = useState({});

  // ── Grid editing (CLW parity) ──────────────────────────────────────
  // Shared edit-state reducer — the same hook CLW's review pane and the
  // copy workflow use, so CellDetailPanel and the shared reviewEdits
  // helpers behave identically here. editedRows is rowId → { field: value };
  // pendingDeletions is the legacy remove-ops view of pendingListOps.
  const {
    editedRows, setEditedRows, pendingDeletions, pendingListOps, setPendingListOps,
    pendingRowOps, setPendingRowOps, pendingRowOpCount,
    editingCell, setEditingCell, editingDraft, setEditingDraft,
    getRowValue, commitCellEdit, cancelCellEdit, toggleListDeletion,
    addListItem, editListItem,
    pendingEditCount: pendingScalarEditCount, pendingListOpCount, resetEdits,
  } = useReviewGridEdits();
  const [detailRowId, setDetailRowId] = useState(null);
  const [detailCellField, setDetailCellField] = useState(null);
  // Flagged Items tab (plan review): which items are expanded, which the user
  // skipped, and the per-item question typed into the ask-the-agent box.
  const [openFlags, setOpenFlags] = useState(() => new Set());
  const [skippedFlags, setSkippedFlags] = useState(() => new Set());
  const [flagAsk, setFlagAsk] = useState({});
  const [savingEdits, setSavingEdits] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [launchSuccess, setLaunchSuccess] = useState(false);
  const [launchErr, setLaunchErr] = useState(null);
  // Bulk audience refresh (TTD / DV360) — re-resolves every audience row
  // against the live platform API in one server-side job and overlays the
  // result onto the plan. { status: 'idle'|'running'|'done'|'error', … }.
  const [audRefresh, setAudRefresh] = useState({ status: 'idle' });
  // Agent sidebar width — wider default than CLW's 360, draggable via the
  // sidebar's left-edge handle, persisted across sessions.
  const [aiWidth, setAiWidth] = useState(() => {
    try {
      const saved = Number(window.localStorage.getItem('lcd-ai-panel-w'));
      if (saved >= 360 && saved <= 960) return saved;
    } catch (e) { /* private mode etc. */ }
    return 520;
  });

  // Name/destination/stage from the same endpoint that feeds the Launch
  // board — the stage picks which view (plan review vs DSP reconcile) renders.
  useEffect(() => {
    if (!accountId || !campaignId) { setCampaignLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        // One indexed row, not the whole board. This used to GET /launch-list
        // and .find() its campaign, which made opening one campaign pay for a
        // launcher-result download per campaign in the account.
        const res = await api.get(getApiUrl(`/api/v2/campaigns/${campaignId}/stage`), {
          params: { accountId, userId },
        });
        const found = res.data?.campaign || null;
        if (!cancelled && found) setCampaign(found);
      } catch (e) { /* header falls back to placeholders */ } finally {
        if (!cancelled) setCampaignLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [accountId, userId, campaignId]);

  // A GroundTruth IO that launched successfully opens in its launched
  // campaign workspace (actual GT state + activity), not this plan view.
  useEffect(() => {
    if (demoMode || !accountId || !campaignId) return undefined;
    let cancelled = false;
    findGtLaunchedWorkspace({ campaignId, accountId, userId }).then((target) => {
      if (!cancelled && target) navigate(`/campaigns/${encodeURIComponent(target)}`, { replace: true });
    });
    return () => { cancelled = true; };
  }, [demoMode, accountId, userId, campaignId, navigate]);

  // Plan-side data: the exact source CLW's review pane uses, flattened with
  // CLW's own row builders so field semantics stay identical.
  useEffect(() => {
    if (demoMode || !accountId || !campaignId) { setPlanLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        let lr = null;
        let cts = null;
        let alreadyLaunched = false;
        let platformCampaignId = null;
        try {
          const res = await api.get(getApiUrl(`/api/v2/campaigns/${campaignId}/planning-review`), {
            params: { accountId, userId },
          });
          res.data = await ensureYahooReviewPrepared(res.data, { api, getApiUrl, campaignId, accountId, userId });
          lr = res.data?.launcherResult || null;
          cts = res.data?.ctsReview || null;
          alreadyLaunched = Boolean(res.data?.alreadyLaunched);
          platformCampaignId = res.data?.campaign?.platform_campaign_id || null;
        } catch (e) { if (e.yahooPreparation) throw e; /* fall through to the snapshot */ }
        if (!lr && !cts) {
          // GT CTS-review drafts persist their review under
          // campaigns/{id}/cts-review.json (same fallback CLW uses when
          // hydrating a draft cross-device).
          try {
            const snap = await api.get(getApiUrl(`/api/v2/campaigns/${campaignId}/cts-snapshot`), {
              params: { accountId, userId },
            });
            if (snap.data?.ctsReview && typeof snap.data.ctsReview === 'object') cts = snap.data.ctsReview;
          } catch (e) { /* no snapshot either */ }
        }
        if (isGtLauncherResult(lr)) { cts = gtLauncherToCtsReview(lr); lr = null; }
        let summary = null;
        let rows = [];
        let lrPlatform = null;
        let gadsKind = null;
        if (cts) {
          summary = summarizeReviewCtsResult(cts);
          rows = buildGtReviewRows(cts);
        } else if (lr) {
          // Platform is resolved, not guessed. This used to be a binary
          // "ttd or else dv360" ternary, which sent every Google Ads plan
          // through the DV360 builders — they find no lineItems, produce zero
          // rows, and the page fell through to "No parsed plan available".
          lrPlatform = resolveLrPlatform(lr);
          if (lrPlatform === 'gads') {
            gadsKind = gadsResultSubtype(lr);
          }
          ({ summary, rows } = rebuildPlan(lr, lrPlatform));
        }
        if (!cancelled && rows.length) {
          // ctsReview is kept alongside launcherResult so grid edits can be
          // persisted through the same two backends CLW writes to (see
          // saveGridEdits).
          setPlan({ summary, rows, alreadyLaunched, launcherResult: lr, ctsReview: cts, lrPlatform, gadsKind, platformCampaignId });
        }
      } finally {
        if (!cancelled) setPlanLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [demoMode, accountId, userId, campaignId]);

  useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    if (reconPollTimer.current) clearTimeout(reconPollTimer.current);
  }, []);

  const toast = (msg) => {
    setToastMsg(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(null), 3200);
  };

  // ── Reconciliation (plan vs live DSP state; TTD/DV360) ──
  const fetchReconciliation = async () => {
    try {
      const res = await api.get(getApiUrl(`/api/v2/campaigns/${campaignId}/reconciliation`), {
        params: { accountId, userId },
      });
      const data = res.data || {};
      setRecon(data);
      if (data.status === 'synced') {
        setReconSyncing(false);
        setResolved(Object.fromEntries(
          Object.entries(data.resolutions || {}).map(([k, v]) => [k, v.mode || 'flag']),
        ));
        setSignoff(data.signoff || null);
      }
      return data;
    } catch (e) {
      return null;
    } finally {
      setReconKnown(true);
    }
  };

  // Nothing exists in the DSP until the campaign is launched, and GroundTruth
  // has no reconcile handler at all — in both cases the endpoint can only
  // answer "none". Treat an unloaded plan as still-planning so we don't fire
  // before we know. Same test the render branch uses for `planStage`.
  const stillPlanning = !plan
    || Boolean(plan.ctsReview)
    || campaign?.stage === 'plan_ready'
    || campaign?.stage === 'plan_in_review'
    || (campaign?.stage !== 'dsp_ready' && !plan.alreadyLaunched);

  useEffect(() => {
    if (demoMode || !accountId || !campaignId) return;
    // `plan` still loading — we genuinely don't know yet, so leave reconKnown
    // false and let the header stay neutral.
    if (!plan) return;
    if (stillPlanning) { setReconKnown(true); return; }
    fetchReconciliation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demoMode, accountId, campaignId, plan, stillPlanning]);

  const pollReconciliation = (attempt = 0) => {
    if (reconPollTimer.current) clearTimeout(reconPollTimer.current);
    reconPollTimer.current = setTimeout(async () => {
      const data = await fetchReconciliation();
      if (data && data.status === 'synced') {
        toast('✓ DSP state synced');
      } else if (data && data.status === 'error') {
        setReconSyncing(false);
        toast(`⚠ DSP sync failed${data.message ? `: ${data.message}` : ''}`);
      } else if (attempt < 36) { // ~3 minutes at 5s
        pollReconciliation(attempt + 1);
      } else {
        setReconSyncing(false);
        toast('⚠ DSP sync is taking longer than expected — try again shortly');
      }
    }, 5000);
  };

  const syncNow = async () => {
    if (reconSyncing) return;
    try {
      setReconSyncing(true);
      toast('Syncing live state from the DSP…');
      await api.post(getApiUrl(`/api/v2/campaigns/${campaignId}/reconcile-sync`), { accountId, userId });
      pollReconciliation();
    } catch (e) {
      setReconSyncing(false);
      toast(`⚠ ${e.response?.data?.error || 'Failed to start DSP sync'}`);
    }
  };

  // Auto-refresh on open when the last DSP sync is over a day old (or never
  // ran) so the page never presents day-stale numbers as current. Fires at
  // most once per campaign per mount — autoSyncedRef guards the re-render
  // that the sync itself triggers.
  //
  // Layout effect, not a passive one: it runs in the same commit that first
  // delivers `recon`, before paint. As a useEffect it ran *after* paint, so a
  // never-synced campaign rendered one frame of "Never refreshed" before
  // flipping to "Refreshing…" — the flicker.
  useLayoutEffect(() => {
    if (demoMode || !accountId || !campaignId) return;
    if (!recon || reconSyncing) return;
    if (autoSyncedRef.current === campaignId) return;
    // GET /reconciliation answers 'none' both for "never synced" and for
    // "platform has no reconcile handler", so check the destination before
    // firing — otherwise every load of a non-TTD/DV360 campaign would kick
    // off a sync the API rejects.
    if (!pushPlatformKey(campaign?.destination)) return;
    if (stillPlanning) return;
    const syncedAt = recon.syncedAt || recon.synced_at || null;
    const ageMs = syncedAt ? Date.now() - new Date(syncedAt).getTime() : Infinity;
    if (ageMs <= STALE_SYNC_MS) return;
    autoSyncedRef.current = campaignId;
    toast(syncedAt
      ? 'Last DSP sync is over a day old — refreshing…'
      : 'No DSP sync yet — refreshing…');
    syncNow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demoMode, accountId, campaignId, recon, campaign?.destination, campaign?.stage, plan]);

  // ── Sidekick agent (same reviewer session CLW uses for TTD/DV360) ──
  // The session gets the raw launcher result inline; this page is
  // read-only, so suggestion chips point the user at CLW instead of
  // applying edits here.
  //
  // Deliberately TTD/DV360-only: the reviewer session's prompts and
  // suggestion schema are written against those two plan shapes, so handing
  // it a Google Ads plan would produce confident nonsense. Google Ads plans
  // get the sidebar's fallback message instead.
  const agentPayload = useMemo(() => (
    // isMmgyAccount gates the whole payload, not just autoStart, so an on-demand
    // bootstrap (the sidebar creates a session when the user types before
    // autoStart fires) cannot reach the reviewer either.
    !demoMode && isMmgyAccount
      && plan?.launcherResult && (plan?.lrPlatform === 'ttd' || plan?.lrPlatform === 'dv360')
      ? {
          destination: plan.lrPlatform, // 'ttd' | 'dv360'
          processingId: campaignId || `launch-details-${plan.lrPlatform}`,
          finalPlanJson: plan.launcherResult,
          campaignName: campaign?.name || plan?.summary?.campaignName || null,
        }
      : null
  ), [demoMode, isMmgyAccount, plan, campaignId, campaign]);
  const agent = useMmgyClwReviewerSession({
    accountId,
    userId,
    // Gated on the QA mode: opening the sidebar in bulk mode must not start the
    // reviewer and run its QA pass in the background.
    autoStart: aiOpen && agentMode === 'qa' && Boolean(agentPayload),
    createPayload: agentPayload || {},
  });
  const agentSession = agentPayload ? agent : null;

  // Same drag mechanics as CLW's startAiResize: dragging the handle left
  // widens the panel; clamped and persisted.
  const startAiResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = aiWidth;
    document.body.classList.add('clw-col-resizing'); // CLW's global cursor/no-select style
    const onMove = (ev) => {
      const next = Math.max(360, Math.min(960, Math.round(startWidth + (startX - ev.clientX))));
      setAiWidth(next);
      try { window.localStorage.setItem('lcd-ai-panel-w', String(next)); } catch (err) { /* ignore */ }
    };
    const onUp = () => {
      document.body.classList.remove('clw-col-resizing');
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // ── Grid editing (CLW parity) ──────────────────────────────────────
  // Platform shape decides which column set, editable allow-list, and
  // editor-type map the detail panel uses — the same pick CLW's review pane
  // makes, now four-way (GT / TTD / DV360 / Google Ads).
  const isGtPlan = Boolean(plan?.ctsReview);
  const planIsTtd = plan?.lrPlatform === 'ttd';
  const isGadsPlan = plan?.lrPlatform === 'gads';
  const isFbPlan = plan?.lrPlatform === 'fb';
  const isYahooPlan = plan?.lrPlatform === 'yahoo';
  // 'search' | 'pmax' | 'demandgen' — each has its own column set because a
  // row means a different entity (ad group vs asset group).
  const gadsKind = isGadsPlan ? (plan?.gadsKind || 'search') : null;
  const gridGroupCols = isYahooPlan ? YAHOO_GROUP_COLS : isGtPlan
    ? GT_GROUP_COLS
    : (isGadsPlan ? gadsGroupCols(gadsKind) : (isFbPlan ? FB_GROUP_COLS : GROUP_COLS));
  const gridGroupKeys = isYahooPlan ? YAHOO_GROUP_KEYS : isGtPlan
    ? GT_GROUP_KEYS
    : (isGadsPlan ? gadsGroupKeys(gadsKind) : (isFbPlan ? FB_GROUP_KEYS : GROUP_KEYS));
  const gridEditableFields = isYahooPlan ? YAHOO_EDITABLE : isGtPlan
    ? GT_EDITABLE
    : (isGadsPlan
        ? gadsEditableFields(gadsKind)
        : (isFbPlan ? FB_EDITABLE : (planIsTtd ? TTD_EDITABLE : DV360_EDITABLE)));
  const gridFieldEditors = isYahooPlan ? YAHOO_FIELD_EDITORS : isGtPlan
    ? GT_FIELD_EDITORS
    : (isGadsPlan
        ? GADS_FIELD_EDITORS
        : (isFbPlan ? FB_FIELD_EDITORS : (planIsTtd ? TTD_FIELD_EDITORS : DV360_FIELD_EDITORS)));
  // Fixed option sets for the GT 'select' editors; other platforms have none.
  const gridFieldOptions = isGtPlan ? GT_STATIC_OPTIONS : null;
  // Why each read-only cell is read-only — shown in the cell detail panel.
  const gridReadOnlyReasons = isYahooPlan ? YAHOO_READ_ONLY_REASONS : isGtPlan
    ? GT_READ_ONLY_REASONS
    : (isGadsPlan
        ? gadsReadOnlyReasons(gadsKind)
        : (isFbPlan ? FB_READ_ONLY_REASONS : (planIsTtd ? TTD_READ_ONLY_REASONS : DV360_READ_ONLY_REASONS)));
  // Per-platform list add/edit/remove capabilities for the cell detail
  // panel. GT's come from the field descriptor (goal 3).
  const gridListFields = isYahooPlan ? YAHOO_LIST_FIELDS : isGtPlan
    ? GT_LIST_FIELDS
    : (isGadsPlan
        ? gadsListFields(gadsKind)
        : (isFbPlan ? FB_LIST_FIELDS : (planIsTtd ? TTD_LIST_FIELDS : DV360_LIST_FIELDS)));

  // ── Bulk editor agent (default sidebar mode) ────────────────────────
  // Same wiring as CLW's review pane: the agent proposes, the user confirms in
  // BulkEditPanel, and the approved batch is folded into the SAME
  // editedRows / pendingListOps this page already saves through
  // applyLauncherResultEdits / applyGtConfigEdits → PUT /planning-review.
  //
  // Facebook sends 'facebook', not the 'fb' resolveLrPlatform returns — that is
  // the spelling services/bulkEdit/fieldMap.js#PLATFORMS and the agent_sessions
  // CHECK constraint use. Same mapping as CLW's review pane; keep the two in
  // step.
  const bulkPlatform = isYahooPlan ? 'yahoo' : isGtPlan
    ? 'groundtruth'
    : (isGadsPlan ? 'gads' : (isFbPlan ? 'facebook' : (planIsTtd ? 'ttd' : 'dv360')));
  const planAlreadyLaunched = Boolean(plan?.alreadyLaunched);
  const yahooAlreadyLaunched = isYahooPlan && planAlreadyLaunched;
  // Row noun for the agent's orientation line. Computed here rather than
  // reusing `rowNoun`, which is declared below and would be in its temporal
  // dead zone when this memo first runs.
  const bulkRowLabel = isYahooPlan ? 'line flights' : isGadsPlan
    ? (GADS_ROW_LABEL[gadsKind] || 'ad groups')
    : (isFbPlan ? FB_ROW_LABEL : 'ad groups');
  const bulkDigest = useMemo(
    () => buildBulkDigest(plan?.rows || [], gridEditableFields),
    [plan?.rows, gridEditableFields],
  );
  // ── Row selection — the agent's scope ────────────────────────────────────
  // Ticked rows narrow every edit the bulk editor proposes. Pruned whenever the
  // rows are rebuilt: rowIds are POSITIONAL on GroundTruth and Google Ads, so a
  // selection carried across a save could re-point at different entities.
  const [selectedRowIds, setSelectedRowIds] = useState(() => new Set());
  useEffect(() => {
    setSelectedRowIds((prev) => {
      if (prev.size === 0) return prev;
      const live = rowIdSet(plan?.rows || []);
      const next = new Set([...prev].filter((id) => live.has(String(id))));
      return next.size === prev.size ? prev : next;
    });
  }, [plan?.rows]);
  const selectedRowIdList = useMemo(() => [...selectedRowIds], [selectedRowIds]);

  // One-step undo for an auto-saved batch — see revertUndo below.
  const [undoEntry, setUndoEntry] = useState(null);
  const [undoing, setUndoing] = useState(false);

  // GroundTruth bulk editor: proposals whose auto-save failed. Their edits stay
  // pending in the grid, so the next successful save commits them and their
  // cards move to "Applied and saved"; a discard throws them away.
  const failedAutoSaveIdsRef = useRef(new Set());
  const reportProposalOutcomeRef = useRef(null); // wired to the bulk agent below
  const settleFailedAutoSaves = (committed) => {
    const ids = [...failedAutoSaveIdsRef.current];
    failedAutoSaveIdsRef.current.clear();
    ids.forEach((id) => (committed
      ? reportProposalOutcomeRef.current?.(id, 'saved')
      // Still "Save failed", but no longer "press Save to retry": there is
      // nothing pending to save any more.
      : reportProposalOutcomeRef.current?.(id, 'save_failed', 'Not saved — the pending changes were discarded.')));
    return committed ? ids : [];
  };

  const bulkCreatePayload = useMemo(() => (
    !demoMode && campaignId && bulkDigest.rows.length && bulkPlatform && !(isYahooPlan && planAlreadyLaunched)
      ? {
          platform: bulkPlatform,
          ...(isYahooPlan ? { alreadyLaunched: planAlreadyLaunched } : {}),
          surface: 'clw',
          campaignId,
          campaignName: campaign?.name || plan?.summary?.campaignName || null,
          rows: bulkDigest.rows,
          editableFields: bulkDigest.editableFields,
          // List-op capabilities — a field can grant add/remove while having no
          // scalar writer (Copy TTD geo), so the agent needs both lists.
          listFields: serializeListFields(gridListFields),
          rowLabel: bulkRowLabel,
          // The user's ticked rows. A HARD scope on every edit the agent
          // proposes — enforced in services/bulkEdit/selector.js, not left to the
          // prompt.
          selectedRowIds: selectedRowIdList,
          // Except for Yahoo, launch state is deliberately not sent — bulk editing is available in
          // every state. See bulkOps.js validateStaging.
          launcherResult: isGtPlan ? (plan?.ctsReview?.result || null) : (plan?.launcherResult || null),
        }
      : null
  ), [
    demoMode, campaignId, campaign, plan, bulkPlatform, bulkDigest, bulkRowLabel, isYahooPlan, planAlreadyLaunched,
    isGtPlan, selectedRowIdList, gridListFields,
  ]);
  const bulkAgent = useBulkEditorSession({
    accountId,
    userId,
    autoStart: aiOpen && agentMode === 'bulk' && Boolean(bulkCreatePayload),
    createPayload: bulkCreatePayload || {},
  });
  reportProposalOutcomeRef.current = bulkAgent.gtChat ? bulkAgent.reportProposalOutcome : null;

  // ── Optimization agent (replaces the Review QA tab) ──────────────────────
  // Reads a finished campaign-optimizer run, summarises it, and applies the
  // suggestions the user picks — straight to the live platform campaign. The hook
  // resolves its own eligibility (an optimizer for the destination AND a launched
  // campaign), so the tab explains itself when unavailable instead of failing.
  const optimizeAgent = useOptimizationSession({
    accountId,
    userId,
    campaignId,
    destination: campaign?.destination || plan?.summary?.platform || null,
    campaignName: campaign?.name || plan?.summary?.campaignName || null,
    // The same `optimize-campaign` gate the Optimize Campaign button reads.
    accountSettings,
    enabled: aiOpen && agentMode === 'optimize' && !demoMode,
  });
  // A withheld tab (or a plan whose platform resolved late) must not strand the
  // user in a mode with no agent behind it.
  useEffect(() => {
    if (!isSelectableMode(agentMode)) setAgentMode('bulk');
    else if (agentMode === 'qa' && !QA_AGENT_TAB_ENABLED) setAgentMode('bulk');
  }, [agentMode]);

  const stagedBatchTs = useRef(null);
  // GroundTruth: row add/delete ops per staged preview, so an Undo that also
  // reverts an earlier batch's row ops knows the row set will change.
  const rowOpsByPreviewRef = useRef(new Map());
  const [bulkStageNote, setBulkStageNote] = useState(null);
  // Set by the staging effect, consumed by the auto-save effect below it.
  const [autoSaveRequest, setAutoSaveRequest] = useState(null);
  const [autoSaveOutcome, setAutoSaveOutcome] = useState(null); // { ts, ok }
  useEffect(() => {
    const batch = bulkAgent.lastStagedBatch;
    if (!batch || batch.ts === stagedBatchTs.current) return;
    stagedBatchTs.current = batch.ts;
    const { edits, listOps, rowOps, refused, accepted } = validateStaging(batch.staging, {
      platform: bulkPlatform, reviewRows: plan?.rows, selectedRowIds, alreadyLaunched: planAlreadyLaunched,
      editableFields: gridEditableFields,
      listFields: serializeListFields(gridListFields),
      knownRowIds: rowIdSet(plan?.rows || []),
    });
    if (Object.keys(edits).length) setEditedRows((prev) => mergeEdits(prev, edits));
    if (Object.keys(listOps).length) setPendingListOps((prev) => mergeListOps(prev, listOps));
    if (rowOps.length) setPendingRowOps((prev) => mergeRowOps(prev, rowOps));
    const rowOpNote = rowOps.length
      ? ` Includes ${rowOps.length} row add/delete op(s).`
      : '';
    setBulkStageNote({
      accepted,
      refused,
      // "Applied", not "Saved": the save has not run yet. The outcome is
      // appended at render time from autoSaveOutcome, so a failed save cannot
      // leave a note claiming the change was persisted.
      text: accepted
        ? `Applied ${accepted} change${accepted === 1 ? '' : 's'}.${rowOpNote}`
        : 'Nothing was staged.',
      ts: batch.ts,
      // GroundTruth: which proposal card an Undo on this note belongs to.
      previewId: batch.preview_id || null,
    });
    if (batch.preview_id) rowOpsByPreviewRef.current.set(batch.preview_id, rowOps.length);
    // GroundTruth: a batch the grid refused entirely never saves — its card
    // says so rather than spinning on "Applying…".
    if (!accepted && isGtPlan) {
      bulkAgent.reportProposalOutcome?.(
        batch.preview_id,
        'save_failed',
        `Nothing could be applied to the grid.${refused?.length ? ` ${refused.map((r) => r.reason).join(' ')}` : ''}`,
      );
    }
    // AUTO-SAVE (#3). Pressing Confirm on the preview IS the decision; a second
    // trip to the Save button is the same confirmation twice, and an unsaved batch
    // is one refresh from being lost. Undo is what makes it safe, and the same
    // call arms it.
    //
    // Requested via state because the setEditedRows updates above are still
    // queued at this point — saveGridEdits called here would close over the
    // pre-batch edits and save nothing.
    if (accepted) {
      setAutoSaveRequest({
        ts: batch.ts,
        label: batch.summary || `${accepted} agent change${accepted === 1 ? '' : 's'}`,
        previewId: batch.preview_id || null,
        // A partly-refused batch must not read as wholly "Applied and saved".
        partial: refused?.length
          ? `${accepted} of ${accepted + refused.length} changes applied — skipped: ${refused.map((r) => r.reason).join(' ')}`
          : null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkAgent.lastStagedBatch]);

  // The other half of the auto-save. Deps are `autoSaveRequest` ALONE: the
  // staging effect sets it in the same batch as the edits, so this runs on the
  // render where both have landed, and the saveGridEdits it closes over there
  // already sees them. Naming pendingEditCount here instead looked more explicit
  // but is a load-bearing mistake — dep arrays are evaluated during render, and
  // pendingEditCount is declared further down, so it threw a TDZ ReferenceError
  // before the component could mount.
  useEffect(() => {
    if (!autoSaveRequest) return;
    const req = autoSaveRequest;
    setAutoSaveRequest(null);
    setAutoSaveOutcome(null);
    (async () => {
      const ok = await saveGridEdits({ captureUndo: true, undoLabel: req.label, undoPreviewId: req.previewId });
      setAutoSaveOutcome({ ts: req.ts, ok: ok !== false });
      // GroundTruth: the proposal card shows the outcome, and keeps showing it
      // after a reload. A failed one stays pending in the grid; the next
      // successful save flips its card to saved (settleFailedAutoSaves).
      if (isGtPlan && req.previewId) {
        if (ok === false) failedAutoSaveIdsRef.current.add(req.previewId);
        bulkAgent.reportProposalOutcome?.(
          req.previewId,
          ok !== false ? 'saved' : 'save_failed',
          ok !== false ? req.partial : 'The save failed — your changes are still pending; press Save to retry.',
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSaveRequest]);

  // Keep the agent's rows current; a stale digest can point a preview at the
  // wrong row on GroundTruth / Google Ads, whose row ids are positional.
  useEffect(() => {
    if (!bulkAgent.sessionId || !bulkDigest.rows.length) return;
    const t = setTimeout(() => {
      bulkAgent.refreshDigest({
        rows: bulkDigest.rows,
        ...(isYahooPlan ? { alreadyLaunched: planAlreadyLaunched } : {}),
        editableFields: bulkDigest.editableFields,
        // Ticking a row re-scopes the agent through this refresh — no separate
        // call, and no agent turn spent on it.
        selectedRowIds: selectedRowIdList,
      });
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkAgent.sessionId, bulkDigest, selectedRowIdList, yahooAlreadyLaunched]);
  // What a row and its parent are called, everywhere the page says it: the
  // grid footer, the header sub-line, the summary strip and the QA tables.
  // Google Ads PMax / Demand Gen rows are asset groups, not ad groups, and
  // their parent is a campaign rather than a media-plan package.
  const rowNoun = isYahooPlan ? 'line flights' : isGadsPlan
    ? (GADS_ROW_LABEL[gadsKind] || 'ad groups')
    : (isFbPlan ? FB_ROW_LABEL : 'ad groups');
  const rowSingular = rowNoun.replace(/s$/, '');
  // Hyphenated form for compound copy ("Full ad-group grid", "Per-ad-group
  // coverage") — identical to the pre-Google-Ads wording for every other
  // platform, so no existing label shifts.
  const rowSingularHyphen = rowSingular.replace(/ /g, '-');
  // Facebook's allocation unit IS the media-plan package (breakdownFbByPackage
  // groups on it), so it keeps the default noun rather than Google Ads'
  // per-campaign one.
  const parentNoun = (isGadsPlan || isYahooPlan) ? 'campaigns' : 'packages';
  const titleCase = (s) => String(s).charAt(0).toUpperCase() + String(s).slice(1);
  const rowNounTitle = titleCase(rowNoun);
  const rowSingularTitle = titleCase(rowSingular);
  const parentNounTitle = titleCase(parentNoun);
  // "1 campaign · 2 ad groups", not "1 campaigns".
  const countLabel = (n, plural) => `${fmtInt(n)} ${Number(n) === 1 ? plural.replace(/s$/, '') : plural}`;

  // getRowValue / commitCellEdit / cancelCellEdit / toggleListDeletion come
  // from useReviewGridEdits above. pendingEditCount keeps its historical
  // meaning here: scalar edits + staged list ops (adds / edits / removes)
  // combined — any staged op counts as dirty.
  // Row ops count too: they gate the Save button and saveGridEdits' early
  // return, so leaving them out would make a staged row delete unsaveable.
  const pendingEditCount = pendingScalarEditCount + pendingListOpCount + pendingRowOpCount;

  const discardGridEdits = () => {
    settleFailedAutoSaves(false);
    resetEdits();
    toast('Changes discarded');
  };

  // ── Push-to-DSP account feature flag ───────────────────────────────
  // Push re-asserts a plan value in the live platform, so it's opt-in per
  // account. The backend is authoritative (it strips canPush from the
  // reconciliation payload and rejects a push resolve with 403) — this gate
  // just avoids offering a button that would be refused. `recon.pushEnabled`
  // is preferred when a sync has run; otherwise fall back to reading the
  // setting directly so the plan-review and demo views agree.
  const pushSetting = getSetting(PUSH_TO_DSP_KEY);
  const pushToDspEnabled = demoMode
    // The demo has no real campaign platform, so any enabled platform counts —
    // enough to demonstrate the flow on an opted-in account.
    ? isEnabledForAnyPlatform(pushSetting)
    : (typeof recon?.pushEnabled === 'boolean'
      ? recon.pushEnabled
      : isEnabledForPlatform(pushSetting, pushPlatformKey(campaign?.destination)));

  // ── Platform lookups (TTD geo / audiences, GT taxonomy) ────────────
  // Same campaign-scoped search endpoints CLW drives; they back the cell
  // detail panel's async picker, which is the only way to add a TTD geo
  // segment / resolved audience / GT taxonomy record (picker-backed adds need
  // a platform id, not a typed name). GT taxonomy reads hit the account cache.
  const runLookup = async (kind, query, ctx = {}) => {
    const path = LOOKUP_PATHS[kind];
    if (!campaignId || !path || !query || demoMode) return [];
    try {
      const params = { accountId, userId, q: query };
      // The picker's user-selected type scope wins; otherwise the field's
      // descriptor (or row context, for Drive-To) decides.
      const types = ctx.types || gtLookupTypesFor(ctx.field, ctx.row);
      if (Array.isArray(types) && types.length) params.types = types.join(',');
      const res = await api.get(getApiUrl(`/api/v2/campaigns/${campaignId}/lookup/${path}`), {
        params,
      });
      return Array.isArray(res?.data?.results) ? res.data.results : [];
    } catch (e) {
      throw new Error(e?.response?.data?.error || e.message || 'Lookup failed');
    }
  };

  // On-demand GT taxonomy cache warmup — same recovery as CLW: never-processed
  // accounts have no cached taxonomy file, so the picker offers a live fetch.
  const refreshLookup = async () => {
    if (!campaignId || demoMode) throw new Error('No campaign to refresh');
    const res = await api.post(
      getApiUrl(`/api/v2/campaigns/${campaignId}/lookup/gt-taxonomy/refresh`),
      { accountId, userId },
    );
    const data = res?.data || {};
    if (data.success !== true) throw new Error(data.error || 'GT taxonomy fetch failed');
  };

  // Launched-campaign save: fold pending grid edits into ONE batched
  // edit-campaign request whose structured ops ride the chat metadata
  // config_payload, and dispatch it to the live platform.
  const saveLaunchedEdits = async () => {
    if (pendingRowOps.length > 0) {
      toast('⚠ Row add / delete / duplicate can\'t be applied to a launched campaign');
      return false;
    }
    const platformCampaignId = plan?.platformCampaignId;
    if (!platformCampaignId) {
      toast('⚠ This campaign has no platform id — edits to a launched campaign need it to reach the DSP');
      return false;
    }
    const requests = buildLaunchedEditRequests({
      editedRows,
      pendingListOps,
      reviewRows: plan?.rows || [],
      destination: campaign?.destination,
    });
    if (requests.length === 0) return false;

    setSavingEdits(true);
    try {
      const result = await dispatchLaunchedEdits({
        api, getApiUrl, campaignId, accountId, userId, platformCampaignId, requests,
      });
      if (!result.ok) {
        toast(`⚠ ${result.error || 'Edit dispatch failed'}`);
        return false;
      }
      settleFailedAutoSaves(true);
      resetEdits();
      toast(`✓ Dispatched ${result.count} edit request${result.count === 1 ? '' : 's'} to the live campaign`);
      return true;
    } finally {
      setSavingEdits(false);
    }
  };

  // Persist grid edits through the same two backends CLW writes to, using the
  // shared reviewEdits mappers so a value saved here lands in exactly the
  // field CLW would have written. Edits target the PLAN (the parsed plan
  // JSON) — pushing a value to the live DSP stays the discrepancy tab's
  // "Push plan to DSP" action.
  const saveGridEdits = async (opts = {}) => {
    // The Save button hands this a click event, so only an explicit object with
    // captureUndo arms the undo affordance. Manual saves don't need one.
    const captureUndo = opts?.captureUndo === true;
    const undoLabel = typeof opts?.undoLabel === 'string' ? opts.undoLabel : null;
    // The GroundTruth bulk-editor proposal this save's undo snapshot belongs
    // to, so an Undo can mark THAT card Undone — and no other save's.
    const undoPreviewId = typeof opts?.undoPreviewId === 'string' ? opts.undoPreviewId : null;
    // Failed auto-saves still pending in the grid ride this save — and its
    // Undo reverts them too, so the undo entry names every proposal it covers.
    const undoPreviewIds = [undoPreviewId, ...failedAutoSaveIdsRef.current].filter(Boolean);
    const batchCount = pendingEditCount;
    if (pendingEditCount === 0) return;
    if (view.isDemo || !campaignId) {
      toast('✓ Demo mode — changes are not persisted');
      resetEdits();
      return;
    }
    // Launched campaigns: the plan is already live, so fold pending edits into
    // batched edit-campaign requests (one per edited row) instead of re-writing
    // the plan template the backend now refuses.
    if (planAlreadyLaunched) {
      return saveLaunchedEdits();
    }
    setSavingEdits(true);
    let planWarnings = [];
    let saved = false;
    try {
      if (isGtPlan) {
        const editedConfig = applyGtConfigEdits(plan.ctsReview.result, editedRows, pendingRowOps, pendingListOps);
        // Edits the applier refused (a non-numeric typed into a numeric GT
        // field) plus rule corrections (placeholder replacement, On Premise
        // exclusion, exclude:false). Reported rather than dropped — silently
        // discarding them is what made a cell revert after a save that said it
        // succeeded.
        const gtDropped = droppedGtEdits(editedConfig);
        const gtWarns = gtEditWarnings(editedConfig);
        if (gtDropped.length || gtWarns.length) {
          planWarnings = [
            ...gtDropped.map((d) => ({ message: d.reason })),
            ...gtWarns.map((w) => ({ message: `${w.rowId}: ${w.reason}` })),
          ];
        }
        const preEditIssueCount = (Array.isArray(plan.ctsReview?.errors) ? plan.ctsReview.errors.length : 0)
          + (Array.isArray(plan.ctsReview?.warnings) ? plan.ctsReview.warnings.length : 0);
        const res = await api.put(getApiUrl(`/api/v2/campaigns/${campaignId}/gt-review-edits`), {
          accountId, userId, editedConfig, revalidate: preEditIssueCount > 0,
        });
        const failedReview = gtCtsFailure(res.data?.ctsReview);
        const nextCts = res.data?.ctsReview && !failedReview
          ? res.data.ctsReview : { ...plan.ctsReview, result: res.data?.config || editedConfig };
        if (failedReview) planWarnings.push({ message: failedReview.message });
        // Snapshot the config as it was BEFORE the edits, taken before setPlan
        // replaces it. Both save paths are whole-document PUTs, so undo is just
        // re-saving this.
        if (captureUndo) {
          setUndoEntry({
            kind: 'gt', snapshot: plan.ctsReview.result, label: undoLabel, count: batchCount, ts: Date.now(),
            previewId: undoPreviewId, previewIds: undoPreviewIds,
          });
        }
        setPlan((prev) => ({
          ...prev,
          ctsReview: nextCts,
          summary: summarizeReviewCtsResult(nextCts),
          rows: buildGtReviewRows(nextCts),
        }));
      } else {
        if (!plan?.launcherResult) { toast('⚠ No parsed plan to save against'); return; }
        const edited = applyLauncherResultEdits(plan.launcherResult, editedRows, pendingListOps, plan.rows, pendingRowOps);
        const res = await api.put(getApiUrl(`/api/v2/campaigns/${campaignId}/planning-review`), {
          accountId, userId, editedLauncherResult: edited,
        });
        if (captureUndo) {
          setUndoEntry({
            kind: 'plan', snapshot: plan.launcherResult, label: undoLabel, count: batchCount, ts: Date.now(),
            previewId: undoPreviewId, previewIds: undoPreviewIds,
          });
        }
        setPlan((prev) => ({
          ...prev,
          launcherResult: edited,
          ...rebuildPlan(edited, prev?.lrPlatform),
        }));
        if (Array.isArray(res?.data?.warnings)) planWarnings = res.data.warnings;
      }
      // A GroundTruth batch whose auto-save failed was still pending — this
      // save committed it.
      settleFailedAutoSaves(true);
      resetEdits();
      saved = true;
      // The save succeeded either way — the server's structural warnings ride
      // the same toast so launch-fatal edits get noticed before launch.
      if (planWarnings.length) {
        setPlanNotices({ severity: 'warning', items: planWarnings });
        toast('✓ Saved — see the warnings above');
      } else {
        setPlanNotices(null);
        toast('✓ Changes saved to the campaign plan');
      }
    } catch (e) {
      // A 400 carrying `blockers` is the save-time sanity pass refusing a plan
      // with dangling entity references.
      const blockers = e?.response?.data?.blockers;
      if (Array.isArray(blockers) && blockers.length) {
        // One row per blocker, not three sentences joined by a space.
        setPlanNotices({ severity: 'error', items: blockers });
        toast('⚠ Save refused — see the details above');
      } else {
        setPlanNotices(null);
        toast(`⚠ ${e?.response?.data?.error || e.message || 'Save failed'}`);
      }
    } finally {
      setSavingEdits(false);
    }
    return saved;
  };

  /**
   * Undo the last auto-saved batch by re-saving the snapshot taken just before it.
   * Rides the same PUT as a normal save, so the undo is itself persisted and
   * itself reversible.
   *
   * Refuses while edits are pending: those were made AFTER the batch, and
   * re-PUTing the old document would discard them silently.
   */
  // Resolves true once the revert landed, false otherwise (the GroundTruth bulk
  // editor marks the undone proposal's card from it).
  const revertUndo = async () => {
    if (!undoEntry || !campaignId) return false;
    if (pendingEditCount > 0) {
      toast('⚠ Undo skipped — you have unsaved edits that would be lost. Save or discard them first.');
      return false;
    }
    setUndoing(true);
    try {
      if (undoEntry.kind === 'gt') {
        const res = await api.put(getApiUrl(`/api/v2/campaigns/${campaignId}/gt-review-edits`), {
          accountId, userId, editedConfig: undoEntry.snapshot, revalidate: true,
        });
        const nextCts = res.data?.ctsReview && !gtCtsFailure(res.data.ctsReview)
          ? res.data.ctsReview : { ...plan.ctsReview, result: res.data?.config || undoEntry.snapshot };
        setPlan((prev) => ({
          ...prev,
          ctsReview: nextCts,
          summary: summarizeReviewCtsResult(nextCts),
          rows: buildGtReviewRows(nextCts),
        }));
      } else {
        await api.put(getApiUrl(`/api/v2/campaigns/${campaignId}/planning-review`), {
          accountId, userId, editedLauncherResult: undoEntry.snapshot,
        });
        setPlan((prev) => ({
          ...prev,
          launcherResult: undoEntry.snapshot,
          ...rebuildPlan(undoEntry.snapshot, prev?.lrPlatform),
        }));
      }
      setUndoEntry(null);
      toast('✓ Change undone');
      return true;
    } catch (e) {
      toast(`⚠ ${e?.response?.data?.error || e.message || 'Undo failed'}`);
      return false;
    } finally {
      setUndoing(false);
    }
  };

  // Undo on the latest batch's result note. On GroundTruth the proposal card
  // that batch came from is marked Undone once the revert actually landed.
  // Only when the snapshot being restored IS that batch's; undoing a row-op
  // batch is the surface's own row change, so the conversation survives it.
  const undoBulkBatch = async () => {
    const previewId = bulkStageNote?.previewId;
    const ownsSnapshot = Boolean(previewId) && undoEntry?.previewId === previewId;
    const covered = Array.isArray(undoEntry?.previewIds) && undoEntry.previewIds.length
      ? undoEntry.previewIds : [previewId];
    // Any batch the snapshot reverts may have added or deleted rows — not only
    // this note's own (an earlier failed batch rode the same save).
    const armed = ownsSnapshot && covered.some((id) => (rowOpsByPreviewRef.current.get(id) || 0) > 0);
    if (armed) bulkAgent.expectRowChange?.();
    const ok = await revertUndo();
    if (!ok && armed) bulkAgent.cancelExpectedRowChange?.();
    // Every proposal that save committed is reverted with it — including an
    // earlier batch whose own auto-save had failed.
    if (ok && ownsSnapshot) covered.forEach((id) => bulkAgent.reportProposalOutcome?.(id, 'undone'));
  };

  // Approve & launch without a detour through CLW. Same contract CLW's
  // approveAndLaunch uses: persist pending edits first, then planning-launch,
  // then drop the user on the campaign page.
  //
  // GroundTruth is deliberately excluded — its launch path
  // (launchGroundTruthCampaign) needs the original uploaded Excel/PDF gcsPath
  // and a draft-cleanup step that only CLW's upload state carries. Offering a
  // button here that silently took a different path would be worse than
  // sending GT users to the surface that owns that flow.
  const canLaunchHere = Boolean(campaignId) && !demoMode && !isGtPlan;

  // ── Refresh audiences (TTD / DV360) ─────────────────────────────────
  // Same two endpoints CLW drives: POST kicks off a server-side job that
  // re-resolves every audience term against the live platform, GET polls for
  // the overlaid launcher result. Only offered while the campaign is still in
  // plan review — the backend rejects the mutation with 409 once launched,
  // which is the same reason CLW hides it there.
  // Read the platform off the parsed plan, the same source CLW checks
  // (launcherResult.platform) — campaign.destination can carry other spellings.
  const audPlatform = String(plan?.lrPlatform || '').toLowerCase();
  const canRefreshAudiences = Boolean(campaignId) && !demoMode && !isGtPlan
    && (audPlatform === 'ttd' || audPlatform === 'dv360');

  const pollAudRefresh = (runId, termCount) => {
    let attempts = 0;
    // Each unique term is one live API search; scale the ceiling with the term
    // count the way CLW does (~4 attempts/term, floor 60, cap 800).
    const MAX = Math.min(800, Math.max(60, Math.ceil((termCount || 0) * 4)));
    const tick = async () => {
      attempts += 1;
      try {
        const res = await api.get(
          getApiUrl(`/api/v2/campaigns/${campaignId}/audiences/refresh/${runId}`),
          { params: { accountId, userId } },
        );
        const data = res.data || {};
        if (data.status === 'running') {
          if (attempts >= MAX) { setAudRefresh({ status: 'error', error: 'Refresh timed out' }); return; }
          setTimeout(tick, 1500);
          return;
        }
        if (data.status === 'error') {
          setAudRefresh({ status: 'error', error: data.error || 'Refresh failed' });
          return;
        }
        if (data.status === 'done') {
          const lr = data.launcherResult;
          if (lr) {
            setPlan((prev) => ({
              ...prev,
              launcherResult: lr,
              ...rebuildPlan(lr, prev?.lrPlatform),
            }));
          }
          const matches = data.matches || {};
          // Failed lookups are absent from `matches` (see CLW) — use the count
          // we asked for so the ratio doesn't shrink to hide them.
          const total = termCount || Object.keys(matches).length;
          const matched = Object.values(matches).filter(Boolean).length;
          setAudRefresh({ status: 'done', matched, total });
          toast(`✓ Audiences refreshed — ${matched}/${total} matched`);
        }
      } catch (e) {
        setAudRefresh({ status: 'error', error: e?.response?.data?.error || e.message || 'Refresh failed' });
      }
    };
    setTimeout(tick, 1500);
  };

  const refreshAudiences = async () => {
    if (!canRefreshAudiences || audRefresh.status === 'running') return;
    setAudRefresh({ status: 'running' });
    try {
      const res = await api.post(
        getApiUrl(`/api/v2/campaigns/${campaignId}/audiences/refresh-all`),
        // userId is required by the extractUser middleware on this router.
        { accountId, userId, platform: audPlatform, force: true },
      );
      const data = res.data || {};
      if (data.status === 'noop') {
        setAudRefresh({ status: 'idle' });
        toast('No audience terms on this plan — nothing to refresh');
        return;
      }
      if (!data.runId) throw new Error('Server did not return a runId');
      pollAudRefresh(data.runId, data.termCount);
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || 'Could not start refresh';
      setAudRefresh({ status: 'error', error: msg });
      toast(`⚠ ${msg}`);
    }
  };

  const approveAndLaunch = async () => {
    if (!campaignId || launching) return;
    setLaunchErr(null);
    setLaunching(true);
    try {
      if (pendingEditCount > 0) await saveGridEdits();
      await api.post(getApiUrl(`/api/v2/campaigns/${campaignId}/planning-launch`), {
        accountId, userId,
        metadata: { source: 'launch-campaign-details' },
      });
      setLaunchSuccess(true);
      toast('✓ Approved — launching the campaign');
      setTimeout(() => navigate(`/campaigns/${campaignId}`), 1200);
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || 'Launch failed';
      setLaunchErr(msg);
      toast(`⚠ ${msg}`);
    } finally {
      setLaunching(false);
    }
  };

  const renderAgentSidebar = (kind) => (
    // `clw` class: CLW defines every CSS variable its .clw-ai-* styles use
    // (--g1..--g5, --navy, fonts, radii) on the .clw root — without it the
    // sidebar renders unstyled (e.g. invisible white-on-white user bubbles).
    <div className={`lcd-ai-wrap clw ${aiOpen ? 'open' : ''}`} style={{ '--ai-panel-w': `${aiWidth}px` }}>
      <AgentSidebar
        open={aiOpen}
        setOpen={setAiOpen}
        onResizeStart={startAiResize}
        session={agentMode === 'bulk'
          ? bulkAgent
          // Until a run is loaded there is no optimization session — the sidebar
          // renders OptimizationRunGate through `fallbackMessage` instead.
          : (agentMode === 'optimize'
            ? (optimizeAgent.phase === OPT_PHASE.READY ? optimizeAgent : null)
            : agentSession)}
        modes={[
          BULK_TAB,
          // Review QA is hidden by flag, not deleted — see agent/agentTabs.js.
          // The reviewer hook above stays wired and working; only this entry is
          // withheld.
          ...(QA_AGENT_TAB_ENABLED ? [{
            key: 'qa',
            label: 'Review QA',
            title: 'Explain the parsed plan and its flagged items',
            available: Boolean(agentSession),
            unavailableReason: isMmgyAccount
              ? 'The reviewer agent covers TTD and DV360 campaigns'
              : 'The MMGY reviewer is only available to MMGY accounts — bulk edit works here',
          }] : []),
          {
            key: 'optimize',
            label: 'Optimization',
            title: 'Review campaign analysis and apply the suggestions you pick',
            // Always selectable: the panel behind it explains an ineligible
            // campaign in a sentence the user can read, which beats a disabled tab
            // whose tooltip they have to hunt for.
            available: !isGtPlan,
            unavailableReason: 'GroundTruth campaigns have no optimizer — bulk edit works here',
          },
        ]}
        mode={agentMode}
        setMode={setAgentMode}
        feedItems={agentMode === 'bulk' ? [
          // Each notice carries the timestamp of the thing it reports, so the sidebar
          // can interleave it with the conversation instead of pinning it under
          // everything. See agent/feedOrder.js.
          //
          // The confirmation card is the exception: `pin: 'end'` keeps it BELOW
          // the agent's explanation and previewReadyToShow holds it back until that
          // explanation exists — a preview lands on the tool result, seconds before
          // the agent finishes describing it.
          //
          // GroundTruth keeps EVERY proposal card (and its choice cards) in
          // chronological position instead — see agent/gtChatFeed.js. Its Cancel
          // marks the card and tells the agent server-side.
          ...(bulkAgent.gtChat ? buildGtBulkFeedItems(bulkAgent, {
            onConfirm: (id) => bulkAgent.approvePreview(id, 'click'),
            onDismiss: (id) => bulkAgent.dismissPreview(id),
            savesImmediately: true,
          }) : [previewReadyToShow(bulkAgent) && {
            key: `preview-${bulkAgent.activePreview.preview_id}`,
            pin: 'end',
            node: (
              <BulkEditPanel
                preview={bulkAgent.activePreview}
                onConfirm={(id) => bulkAgent.approvePreview(id, 'click')}
                // Mark it dismissed (which also takes the card down) before telling the
                // agent — the message alone left the card up with a live Confirm.
                onDismiss={(id) => {
                  bulkAgent.dismissPreview?.(id);
                  bulkAgent.sendMessage(`I'm not applying preview ${id}. Don't stage it.`);
                }}
                approving={bulkAgent.approving}
                approveError={bulkAgent.approveError}
                // Confirming auto-saves now (#3) — promising a Save step the user
                // no longer has to take would be a lie.
                savesImmediately
              />
            ),
          }]),
          bulkStageNote && {
            key: `staged-${bulkStageNote.ts}`,
            ts: bulkStageNote.ts,
            node: (
              (() => {
                // The save outcome for THIS batch, or null while it is in flight.
                // Matched on ts so a later batch's result cannot relabel an
                // earlier note.
                const saved = autoSaveOutcome && autoSaveOutcome.ts === bulkStageNote.ts
                  ? autoSaveOutcome : null;
                const failed = saved && !saved.ok;
                return (
                  <div className={`clw-bulk-result ${failed || !bulkStageNote.accepted ? 'warn' : 'ok'}`}>
                    <span>
                      {bulkStageNote.text}
                      {bulkStageNote.accepted && !saved && <> Saving…</>}
                      {saved?.ok && <> Saved.</>}
                      {failed && <> The save failed — your changes are still pending, press Save to retry.</>}
                      {bulkStageNote.refused?.length > 0 && (
                        <> Skipped: {bulkStageNote.refused.map((r) => r.reason).join(' ')}</>
                      )}
                    </span>
                    {/* Undo — the counterweight to auto-saving. Offered only once
                        the save landed: on a failure the changes are still
                        pending, so Save is the fix, not Undo. */}
                    {saved?.ok && undoEntry && undoEntry.ts >= bulkStageNote.ts && (
                      <span className="clw-bulk-undo">
                        <button type="button" onClick={bulkAgent.gtChat ? undoBulkBatch : revertUndo} disabled={undoing}>
                          {undoing ? 'Undoing…' : 'Undo'}
                        </button>
                        <button type="button" className="dismiss" onClick={() => setUndoEntry(null)} title="Keep the change">
                          ✕
                        </button>
                      </span>
                    )}
                  </div>
                );
              })()
            ),
          },
          bulkAgent.lastBulkFailure && !bulkAgent.lastBulkFailure.awaitingApproval && {
            key: `failed-${bulkAgent.lastBulkFailure.ts}`,
            ts: bulkAgent.lastBulkFailure.ts,
            node: (
              <div className="clw-bulk-result warn">
                <span>{bulkAgent.lastBulkFailure.error}</span>
              </div>
            ),
          },
        ].filter(Boolean) : agentMode === 'optimize' ? [
          previewReadyToShow(optimizeAgent) && {
            key: `opt-preview-${optimizeAgent.activePreview.preview_id}`,
            pin: 'end',
            node: (
              <BulkEditPanel
                preview={optimizeAgent.activePreview}
                onConfirm={(id) => optimizeAgent.approvePreview(id, 'click')}
                onDismiss={(id) => optimizeAgent.sendMessage(
                  `I'm not applying preview ${id}. Don't apply it.`,
                )}
                approving={optimizeAgent.approving}
                approveError={optimizeAgent.approveError}
              />
            ),
          },
          optimizeAgent.applying && {
            key: 'opt-applying',
            pin: 'end',
            node: (
              <div className="clw-bulk-result ok">
                <span>Sending the edits to the platform…</span>
              </div>
            ),
          },
          optimizeAgent.applyReport && {
            key: `opt-applied-${optimizeAgent.applyReport.ts}`,
            ts: optimizeAgent.applyReport.ts,
            node: (() => {
              const results = optimizeAgent.applyReport.results || [];
              const failed = results.filter((r) => !r.ok);
              return (
                <div className={`clw-bulk-result ${failed.length ? 'warn' : 'ok'}`}>
                  <span>
                    {`Dispatched ${results.length - failed.length} of ${results.length} edit run(s) to the platform.`}
                    {' Each takes a minute or two.'}
                    {failed.length > 0 && (
                      <> Failed: {failed.map((f) => `${f.label || `#${f.index}`} — ${f.error}`).join('; ')}</>
                    )}
                  </span>
                </div>
              );
            })(),
          },
          optimizeAgent.lastOptimizationFailure && !optimizeAgent.lastOptimizationFailure.awaitingApproval && {
            key: `opt-failed-${optimizeAgent.lastOptimizationFailure.ts}`,
            ts: optimizeAgent.lastOptimizationFailure.ts,
            node: (
              <div className="clw-bulk-result warn">
                <span>{optimizeAgent.lastOptimizationFailure.error}</span>
              </div>
            ),
          },
        ].filter(Boolean) : null}
        scopeNote={agentMode === 'optimize' && optimizeAgent.phase !== OPT_PHASE.INELIGIBLE ? (
          // See CampaignLaunchWorkflow: these controls have to outlive the gate.
          <OptimizationRunBar
            destination={optimizeAgent.context?.destination || campaign?.destination || null}
            hasRun={Boolean(optimizeAgent.run)}
            running={optimizeAgent.phase === OPT_PHASE.RUNNING}
            disabled={optimizeAgent.phase === OPT_PHASE.INELIGIBLE || optimizeAgent.phase === OPT_PHASE.LOADING}
            disabledReason={optimizeAgent.phase === OPT_PHASE.INELIGIBLE ? optimizeAgent.context?.reason : null}
            onRun={optimizeAgent.startRun}
          />
        ) : agentMode === 'bulk' && selectedRowIds.size > 0 ? (
          <div className="clw-ai-scope">
            <span>
              Scoped to <strong>{selectedRowIds.size}</strong> selected {selectedRowIds.size === 1 ? 'row' : 'rows'} —
              edits won&apos;t touch anything else.
            </span>
            <button type="button" onClick={() => setSelectedRowIds(new Set())}>Clear</button>
          </div>
        ) : null}
        config={agentMode === 'bulk' ? {
          title: 'Bulk editor',
          note: 'edits many rows at once',
          // GroundTruth: plain words and no "Try asking" list, shared with the
          // Campaign Launch Workflow's bulk editor (agent/gtBulkPlaceholder.js).
          placeholder: isYahooPlan ? YAHOO_BULK_PLACEHOLDER : isGtPlan ? gtBulkPlaceholder(rowNoun) : {
            title: 'What do you want to change?',
            body: (
              <>I can change <strong>dates</strong>, <strong>budgets</strong> and <strong>geo</strong> across many {rowNoun} at once. I'll show you exactly what changes and ask before anything is staged.</>
            ),
            sectionLabel: 'Try asking',
            prompts: [
              `Move every ${rowSingular} to a 2026-09-01 → 2026-09-30 flight.`,
              'Which rows have no end date?',
              'Add Denver to the geo on every Texas row.',
              'Cut the budget 15% on the lowest-spending rows.',
            ],
          },
          autoQaMessage: null,
          loadingMessage: `Reading your ${rowNoun} — one moment.`,
          inputPlaceholder: `Describe the change across your ${rowNoun}…`,
          inputPlaceholderNoSession: 'Bulk editor starting up…',
          fallbackMessage: `Your plan has ${plan?.rows?.length || 0} ${rowNoun}. Tell me what to change across them.`,
        } : agentMode === 'optimize' ? {
          title: 'Optimization',
          note: 'reviews analysis and applies approved suggestions',
          placeholder: optimizeAgent.phase === OPT_PHASE.READY ? {
            title: 'What do you want to apply?',
            body: (
              <>I&apos;ve read this campaign&apos;s analysis reports. I can <strong>explain</strong> any
              suggestion, and <strong>apply</strong> the ones you pick — those go straight to the live
              campaign, so I&apos;ll show you exactly what changes and ask first.</>
            ),
            sectionLabel: 'Try asking',
            prompts: [
              'Which suggestions would move the needle most?',
              'Apply the high-priority ones.',
              'Why does it want to change that bid?',
              'Which of these are just advice, not changes?',
            ],
          } : null,
          autoQaMessage: null,
          loadingMessage: 'Reading the campaign analysis reports — summarising the findings.',
          inputPlaceholder: 'Ask about a suggestion, or say which to apply…',
          inputPlaceholderNoSession: optimizeAgent.phase === OPT_PHASE.RUNNING
            ? 'Waiting for campaign analysis…'
            : 'Run campaign analysis to get started',
          // Before a run is loaded there is no session, so this slot carries the
          // run lifecycle: eligibility, the Run button, the spinner, the failure.
          fallbackMessage: (
            <OptimizationRunGate
              phase={optimizeAgent.phase}
              context={optimizeAgent.context}
              runError={optimizeAgent.runError}
              platformLabel={(optimizeAgent.context?.platform || bulkPlatform || '').toUpperCase()}
              // The hook is switched off in demo mode, so it would never leave
              // its initial phase — say so rather than spinning.
              disabledReason={demoMode
                ? 'Optimization runs against a real campaign on the ad platform, so it is not available in demo mode.'
                : null}
            />
          ),
        } : {
          title: 'Sidekick agent',
          note: kind === 'recon' ? 'can explain plan vs DSP' : 'can review the parsed plan',
          placeholder: {
            title: 'How can I help?',
            body: kind === 'recon'
              ? <>I can <strong>explain the launched plan</strong>, walk through <strong>plan-vs-DSP discrepancies</strong>, compare <strong>budgets and flights</strong>, or advise whether to <strong>push or accept</strong> a difference.</>
              : <>I can <strong>explain fields</strong> in the parsed plan, walk through <strong>flagged items</strong>, compare <strong>budgets across ad groups</strong>, or advise what to fix before approval.</>,
            sectionLabel: 'Try asking',
            prompts: kind === 'recon'
              ? [
                'Summarise the launched plan — campaigns, ad groups, budget, flight.',
                'Walk me through the open discrepancies.',
                'Should I push the plan budget or accept the DSP value?',
                'Which ad groups changed since launch?',
              ]
              : [
                'Summarise the plan — campaigns, ad groups, budget, flight.',
                'Walk me through the flagged items.',
                'Which ad groups look misconfigured?',
                'Is this plan ready to approve?',
              ],
          },
          autoQaMessage: null,
          inputPlaceholder: 'Ask about the plan, a discrepancy, or a flagged item…',
          inputPlaceholderNoSession: 'Agent available for TTD / DV360 campaigns…',
          fallbackMessage: isMmgyAccount
            ? 'The Sidekick agent covers TTD and DV360 campaigns. Open this campaign in CLW for the GroundTruth CTS agent.'
            : 'The MMGY reviewer agent is only available to MMGY accounts. Switch to Bulk edit to change dates, budgets or geo across rows.',
          suggestionHint: 'This page is read-only — open the campaign in CLW to apply the agent\'s edits.',
        }}
        context={{ rowsCount: plan?.rows?.length || 0, rowLabel: rowNoun }}
        suggestionStatus={aiSuggestionStatus}
        suggestionErrors={{}}
        onApplySuggestion={() => toast('Suggestions apply in CLW — this page is read-only')}
        onApplyAllSuggestions={() => toast('Suggestions apply in CLW — this page is read-only')}
        onDismissSuggestion={(s) => setAiSuggestionStatus((prev) => ({ ...prev, [s.key]: 'dismissed' }))}
      />
    </div>
  );

  const agentButton = (
    <button
      className={`btn btn-secondary btn-sm lcd-ai-btn ${aiOpen ? 'active' : ''}`}
      onClick={() => setAiOpen((v) => !v)}
    >
      ✦ Sidekick agent
    </button>
  );

  // ── View model: real plan data when we have it, design demo otherwise.
  //    A synced reconciliation overlays live DSP values, discrepancies, and
  //    match counts; without one the DSP side mirrors the plan ("Sync
  //    pending"). ──
  const view = useMemo(() => {
    if (!demoMode && plan) {
      const { summary, rows } = plan;
      const synced = recon?.status === 'synced';
      const diffByName = new Map();
      const mismatchNames = synced ? new Set() : null;
      if (synced) {
        for (const d of (recon.adGroupDiffs || [])) {
          const key = String(d.name || '').trim().toLowerCase();
          diffByName.set(key, d);
          if (d.status === 'mismatch') mismatchNames.add(key);
        }
      }
      const { tacticRows, tacticTotal } = buildTacticRows(rows, mismatchNames);
      const budget = resolveCampaignBudget(summary, campaign, rows);
      return {
        isDemo: false,
        synced,
        syncedAt: synced ? recon.syncedAt : null,
        matchCount: synced ? recon.matchCount : null,
        reconTotal: synced ? recon.totalAdGroups : null,
        dsp: platformLabel(campaign?.destination || summary?.platform),
        totalAdGroups: summary?.adGroupCount ?? rows.length,
        budget: budget.text,
        budgetSub: budget.sub,
        imps: summary?.totalImpressions != null ? fmtInt(summary.totalImpressions) : '—',
        impsSub: 'From campaign plan',
        flight: summary?.flightStart && summary?.flightEnd ? `${summary.flightStart} – ${summary.flightEnd}` : null,
        discs: synced ? (recon.discrepancies || []).map(serverDisc) : [],
        tacticRows,
        tacticTotal,
        setupRows: buildSetupRows(rows, synced ? diffByName : null),
        qaReports: buildQaReports(rows, synced ? diffByName : null),
        gridRows: rows.map((r) => planGridRow(
          r,
          synced ? diffByName.get(String(r.name || '').trim().toLowerCase()) : null,
        )),
      };
    }
    return {
      isDemo: true,
      dsp: 'GroundTruth',
      totalAdGroups: DEMO_TOTAL_AD_GROUPS,
      budget: '$48,200',
      budgetSub: <>Plan: $48,400 · Δ <span className="delta-neg">-$200</span></>,
      imps: '7,862,400',
      impsSub: <>Plan: 7,900,000 · Δ <span className="delta-neg">-37,600</span></>,
      flight: 'Mar 15 – Apr 15',
      discs: DEMO_DISCREPANCIES,
      tacticRows: DEMO_TACTIC_ROWS,
      tacticTotal: DEMO_TACTIC_TOTAL,
      setupRows: DEMO_SETUP_ROWS,
      qaReports: DEMO_QA_REPORTS,
      gridRows: DEMO_RECON_ROWS,
    };
  }, [demoMode, plan, campaign, recon]);

  // Whether plan-vs-live reconciliation exists for this campaign's platform.
  // Only TTD and DV360 have a reconcile handler (see pushPlatformKey), so a
  // launched Google Ads campaign lands on the reconcile view with nothing to
  // sync — offering "Sync now" or a reconciliation report there would just
  // produce an API rejection. The demo dataset ships a full reconciliation.
  const reconcileSupported = view.isDemo || Boolean(pushPlatformKey(campaign?.destination));

  // The detail panel reads from the live plan rows so it always reflects the
  // latest saved state; demo rows carry their srcRow inline.
  const detailRow = detailRowId
    ? (plan?.rows || []).find((r) => r.rowId === detailRowId)
      || (view.gridRows || []).find((g) => g.id === detailRowId)?.srcRow
      || null
    : null;

  const closeCellPanel = () => { setDetailRowId(null); setDetailCellField(null); };

  // ── Shared CLW grid: plan rows + DSP diff as cell annotations ────────
  // The reconcile grid used to render plan/DSP value pairs in a bespoke
  // table. Feeding the diff in as annotations instead lets the shared grid
  // surface it with the machinery it already has — tinted cell, !/? marker,
  // and the popover showing the plan value against what the DSP reports —
  // so the comparison survives the switch without a parallel renderer.
  const gridRowsForClw = useMemo(() => {
    // Demo mode (?frommock=demo) has no parsed plan — unwrap the mock grid
    // rows' srcRow so the shared grid still has something to render.
    const rows = plan?.rows
      || (view.isDemo ? (view.gridRows || []).map((g) => g.srcRow).filter(Boolean) : []);
    if (recon?.status !== 'synced') return rows;

    // Drive off the discrepancy list itself — the same records the
    // Discrepancies tab shows — so the two always agree. Resolved ones drop
    // out, which is what clears their highlight once a user settles them.
    const open = (recon.discrepancies || []).filter((d) => !resolved[d.id]);
    if (open.length === 0) return rows;

    const norm = (s) => String(s || '').trim().toLowerCase();
    const byAdGroup = new Map();   // ad-group name → discrepancies
    const wide = [];               // campaign / IO scope — apply to their rows
    for (const d of open) {
      if (d.scope === 'adgroup') {
        const k = norm(d.entityName);
        if (!byAdGroup.has(k)) byAdGroup.set(k, []);
        byAdGroup.get(k).push(d);
      } else {
        wide.push(d);
      }
    }

    // Same precedence GT uses in gtRowBuilders: the most severe issue owns the
    // cell, everything else stacks in `extra` so the popover can list them.
    const rank = { m: 2, q: 1, i: 0 };
    const add = (anns, col, entry) => {
      const prior = anns[col];
      if (!prior || rank[entry.type] > rank[prior.type]) {
        anns[col] = { ...entry, extra: prior ? [prior, ...(prior.extra || [])] : [] };
      } else {
        prior.extra = [...(prior.extra || []), entry];
      }
    };
    const entryFor = (d) => ({
      type: d.severity === 'high' ? 'm' : 'q',
      msg: d.diff
        ? `${d.title} — DSP reports ${d.diff.dsp ?? '—'}, the plan says ${d.diff.plan ?? '—'}.`
        : d.title,
      category: d.likelyCause,
      raw: d.diff?.plan,
    });

    // Which rows sit under an IO-scope discrepancy. Reconciliation keys those
    // on io.displayName, which rows carry as `ioName`; `market` only holds it
    // when there's no packageName, and `package` is the packageKey — matching
    // on those alone silently dropped every IO-level flight difference.
    const ownsRow = (d, r) => norm(d.entityName) === norm(r.ioName)
      || norm(d.entityName) === norm(r.market)
      || norm(d.entityName) === norm(r.package);
    // A discrepancy that matches no row at all would be invisible in the grid
    // while still counted in the tab. Fall back to showing it on every row —
    // an IO/campaign-level difference does affect all of its ad groups.
    const orphaned = wide.filter((d) => d.scope !== 'campaign' && !rows.some((r) => ownsRow(d, r)));

    return rows.map((r) => {
      const mine = byAdGroup.get(norm(r.name)) || [];
      // Campaign scope covers every ad group; IO scope only its own rows.
      const inherited = wide.filter((d) => d.scope === 'campaign'
        || ownsRow(d, r) || orphaned.includes(d));
      if (mine.length === 0 && inherited.length === 0) return r;
      const anns = { ...(r.annotations || {}) };
      for (const d of [...mine, ...inherited]) {
        const col = DISC_FIELD_TO_COL[d.field];
        if (!col) continue;
        add(anns, col, entryFor(d));
      }
      return { ...r, annotations: anns };
    });
  }, [plan?.rows, recon, resolved, view.isDemo, view.gridRows]);

  // Search + "only rows that differ" applied the same way CLW filters.
  const gridVisibleRows = useMemo(() => {
    let rows = gridRowsForClw;
    const q = gridQuery.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) => String(r.name || '').toLowerCase().includes(q)
        || String(r.market || '').toLowerCase().includes(q)
        || String(r.tactic || '').toLowerCase().includes(q));
    }
    if (gridOnlyMis) rows = rows.filter((r) => Object.keys(r.annotations || {}).length > 0);
    return rows;
  }, [gridRowsForClw, gridQuery, gridOnlyMis]);

  // Plan-review derived tables. These used to be computed in the component
  // body inside the planStage branch, so buildFlaggedIssues (a triple-nested
  // scan with a linear `includes` in its innermost loop) plus the two table
  // builders re-ran on EVERY render — every keystroke in the grid search box,
  // every toast, every reconciliation poll tick. Keyed on plan.rows they now
  // run once per plan load.
  const planDerived = useMemo(() => {
    const rows = plan?.rows || [];
    const flagged = buildFlaggedIssues(rows);
    const qaRowsPlan = buildPlanQaRows(rows);
    return {
      flagged,
      qaRowsPlan,
      formatRows: buildFormatBreakdown(rows),
      cleanRows: rows.filter((r) => Object.keys(r.annotations || {}).length === 0).length,
      pkgCount: new Set(
        rows.map((r) => (r.market && r.market !== '—' ? r.market : r.package))
          .filter((p) => p && p !== '—'),
      ).size,
      questionCount: flagged.filter((i) => i.sev === 'question').length,
      missingCount: flagged.filter((i) => i.sev === 'missing').length,
      qaFlaggedCount: qaRowsPlan.filter((r) => r.issues > 0).length,
    };
  }, [plan?.rows]);

  const clwDetailRow = detailRowId
    ? gridRowsForClw.find((r) => r.rowId === detailRowId) || null
    : null;

  const renderClwGrid = (readOnly = false) => (
    // `clw` is required: every colour in CampaignLaunchWorkflow.css is a
    // custom property scoped to it. `clw-pane review` supplies the
    // has-dpanel padding so the detail panel doesn't overlap the grid.
    <div className={`clw lcd-clw-grid${clwDetailRow ? ' has-dpanel' : ''}`}>
      <div className={`clw-pane review active${clwDetailRow ? ' has-dpanel' : ''}`}>
        <ReviewGrid
          reviewRows={gridRowsForClw}
          visibleRows={gridVisibleRows}
          getRowValue={getRowValue}
          editedRows={editedRows}
          groupCols={gridGroupCols}
          groupKeys={gridGroupKeys}
          cols={gridGroupCols[gridGroup] || gridGroupCols.overview}
          effectiveActiveGroup={gridGroupCols[gridGroup] ? gridGroup : 'overview'}
          isGt={isGtPlan}
          activeGroup={gridGroup}
          setActiveGroup={setGridGroup}
          gridSearch={gridQuery}
          setGridSearch={setGridQuery}
          filterIssuesOnly={gridOnlyMis}
          setFilterIssuesOnly={setGridOnlyMis}
          detailRow={clwDetailRow}
          detailCellField={detailCellField}
          setDetailRowId={setDetailRowId}
          setDetailCellField={setDetailCellField}
          editingCell={editingCell}
          setEditingCell={setEditingCell}
          editingDraft={editingDraft}
          setEditingDraft={setEditingDraft}
          commitCellEdit={commitCellEdit}
          cancelCellEdit={cancelCellEdit}
          editableFields={gridEditableFields}
          fieldEditors={gridFieldEditors}
          fieldOptions={gridFieldOptions}
          readOnlyReasons={gridReadOnlyReasons}
          ctsReview={plan?.ctsReview || null}
          pendingDeletions={pendingDeletions}
          toggleListDeletion={toggleListDeletion}
          listFields={gridListFields}
          pendingListOps={pendingListOps}
          addListItem={addListItem}
          editListItem={editListItem}
          onLookup={runLookup}
          onRefreshLookup={isGtPlan ? refreshLookup : undefined}
          alreadyLaunched={isYahooPlan && planAlreadyLaunched}
          // Row multi-select — scopes every edit the bulk editor proposes.
          selectedRowIds={selectedRowIds}
          onSelectionChange={setSelectedRowIds}
          campaignName={campaign?.name || 'campaign'}
          aiOpen={aiOpen}
          setAiOpen={setAiOpen}
          // Separate namespace so column widths don't clobber CLW's.
          showAgentButton={!isYahooPlan || !planAlreadyLaunched}
          widthsKey={isYahooPlan ? "clw_col_widths_lcd_yahoo" : "clw_col_widths_lcd"}
          // Breakdown tab — same pivot CLW shows: allocation by package /
          // tactic / format / month for TTD+DV360, ad-groups + QA pivot for GT.
          showBreakdownTab
          breakdownSlot={(
            <div className="clw-review-grid-pivot">
              <ConsolidatedReviewSummary
                slot="pivot"
                isGt={isGtPlan}
                summary={plan?.summary}
                launcherResult={plan?.launcherResult}
                ctsReview={plan?.ctsReview}
                reviewRows={gridRowsForClw}
                creativeFiles={[]}
                breakdownView={breakdownView}
                setBreakdownView={setBreakdownView}
                qaPivotKey={qaPivotKey}
                setQaPivotKey={setQaPivotKey}
                destination={campaign?.destination}
                campaignName={campaign?.name || 'campaign'}
                blocking={isGtPlan && Array.isArray(plan?.ctsReview?.errors) && plan.ctsReview.errors.length > 0}
                okCount={gridRowsForClw.length}
                qCount={gridRowsForClw.reduce((n, r) => n + Object.values(r.annotations || {}).filter((a) => a.type === 'q').length, 0)}
                mCount={gridRowsForClw.reduce((n, r) => n + Object.values(r.annotations || {}).filter((a) => a.type === 'm').length, 0)}
                totalImps={gridRowsForClw.reduce((n, r) => n + (Number(r.imps) || 0), 0)}
              />
            </div>
          )}
          // Same footer CLW puts under its grid: what's visible, what's
          // pending, and the save/discard pair — so an edit made in the cell
          // panel can be persisted without leaving the grid.
          summaryBar={(
            <div className="clw-gsumbar">
              <span>
                <strong>{fmtInt(gridVisibleRows.length)}</strong> of {fmtInt(gridRowsForClw.length)} {rowNoun}
              </span>
              <span>Platform: <strong>{view.dsp}</strong></span>
              {readOnly && <span>Live — edits route to the {(isGadsPlan || isFbPlan) ? 'ad platform' : 'DSP'}</span>}
              {pendingEditCount > 0 && (
                <>
                  <span style={{ color: 'var(--gold)' }}>
                    <strong>{pendingEditCount}</strong> unsaved change{pendingEditCount === 1 ? '' : 's'}
                  </span>
                  <button
                    className="clw-btn clw-btn-secondary clw-btn-sm"
                    onClick={discardGridEdits}
                    disabled={savingEdits}
                  >Discard</button>
                  <button
                    className="clw-btn clw-btn-navy clw-btn-sm"
                    onClick={saveGridEdits}
                    disabled={savingEdits}
                  >{savingEdits ? 'Saving…' : `Save ${pendingEditCount} edit${pendingEditCount === 1 ? '' : 's'}`}</button>
                </>
              )}
              {launchErr && <span style={{ color: 'var(--red)' }}>{launchErr}</span>}
              {/* Field-level change history + undo for launched / imported
                  campaigns — every post-launch edit is tracked and reversible. */}
              {planAlreadyLaunched && campaignId && (
                <ChangeLogButton
                  campaignId={campaignId}
                  accountId={accountId}
                  userId={userId}
                />
              )}
              {/* Original uploads (plan, IO, template) from campaign_files. */}
              {campaignId && (
                <SourceFilesButton
                  campaignId={campaignId}
                  accountId={accountId}
                  userId={userId}
                />
              )}
            </div>
          )}
        />
      </div>
    </div>
  );

  // ── Flagged Items (plan review) ──────────────────────────────────────
  // The Sidekick agent publishes suggestions as { key, path, suggested_value,
  // reason }. A flagged item that came from the agent carries the same `path`,
  // which is what lets us show its proposal as this item's suggested fix.
  // Validator-authored findings have no path and therefore no suggestion —
  // the panel invites the user to ask for one instead of inventing it.
  const suggestionForFlag = (item) => {
    if (!item?.path) return null;
    return (agentSession?.suggestions || []).find((s) => s.path === item.path) || null;
  };

  const toggleFlag = (id) => setOpenFlags((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleAllFlags = (items) => setOpenFlags((prev) => {
    const live = items.filter((i) => !skippedFlags.has(i.id)).map((i) => i.id);
    return live.some((id) => !prev.has(id)) ? new Set(live) : new Set();
  });

  const skipFlag = (id) => {
    setSkippedFlags((prev) => new Set(prev).add(id));
    setOpenFlags((prev) => { const next = new Set(prev); next.delete(id); return next; });
    toast('Skipped for now — it stays in the list until the plan is re-validated');
  };

  const unskipFlag = (id) => setSkippedFlags((prev) => {
    const next = new Set(prev);
    next.delete(id);
    return next;
  });

  // Jump to the offending cell in the Full ad-group grid, opening the same
  // detail panel a direct cell click would.
  const viewFlagInGrid = (item) => {
    setTab('grid');
    if (item.rowIds?.[0] && item.field) {
      setDetailRowId(item.rowIds[0]);
      setDetailCellField(item.field);
    }
  };

  // Stage the agent's proposed value as a pending edit on every ad group the
  // finding covers — the same path a manual cell edit takes, so it lands in
  // the unsaved-changes bar and persists through the existing save endpoints
  // rather than writing straight to the plan.
  const applyFlagSuggestion = (item) => {
    const s = suggestionForFlag(item);
    if (!s) return;
    const value = typeof s.suggested_value === 'string'
      ? s.suggested_value
      : JSON.stringify(s.suggested_value);
    setEditedRows((prev) => {
      const next = { ...prev };
      for (const rowId of item.rowIds) {
        if (!rowId) continue;
        next[rowId] = { ...(next[rowId] || {}), [item.field]: value };
      }
      return next;
    });
    setOpenFlags((prev) => { const next = new Set(prev); next.delete(item.id); return next; });
    toast(`✓ Staged on ${item.rowIds.length} ad group${item.rowIds.length === 1 ? '' : 's'} — review and save`);
  };

  const renderFlaggedRow = (item) => {
    const skipped = skippedFlags.has(item.id);
    const isOpen = openFlags.has(item.id) && !skipped;
    const suggestion = suggestionForFlag(item);
    return (
      <React.Fragment key={item.id}>
        <tr
          className={`issue-item disc-item ${isOpen ? 'open' : ''} ${skipped ? 'resolved' : ''}`}
          onClick={() => !skipped && toggleFlag(item.id)}
        >
          <td><span className={item.sev === 'missing' ? 'issue-dot-m' : 'issue-dot-q'} /></td>
          <td>
            <span className={`issue-severity ${item.sev === 'missing' ? 'issue-sev-missing' : 'issue-sev-ambig'}`}>
              {item.sev === 'missing' ? 'Missing' : 'Question'}
            </span>
          </td>
          <td className="issue-text">
            {item.msg} : <em>{flaggedScopeLabel(item)}</em>
            {skipped && (
              <>
                <span className="disc-resolved-badge">Skipped</span>
                <button
                  className="disc-undo-btn"
                  title="Put this flagged item back in the list"
                  onClick={(e) => { e.stopPropagation(); unskipFlag(item.id); }}
                >
                  ↺ Undo
                </button>
              </>
            )}
          </td>
          <td><span className="issue-field-badge">{item.field}</span></td>
          <td><span className="material-icons issue-row-chevron">keyboard_arrow_down</span></td>
        </tr>
        <tr className={`disc-detail-row issue-detail-row ${isOpen ? 'open' : ''}`}>
          <td colSpan={5}>
            <div className="disc-detail">
              <div className="flag-detail-cols">
                {suggestion ? (
                  <div className="flag-suggested-fix">
                    <div className="eyebrow"><span className="material-icons">auto_awesome</span> Suggested fix</div>
                    <div className="flag-fix-headline">
                      {typeof suggestion.suggested_value === 'string'
                        ? suggestion.suggested_value
                        : JSON.stringify(suggestion.suggested_value)}
                    </div>
                    {suggestion.reason && <div className="flag-fix-detail">{suggestion.reason}</div>}
                  </div>
                ) : (
                  <div className="flag-suggested-fix empty">
                    <div className="eyebrow"><span className="material-icons">auto_awesome</span> Suggested fix</div>
                    <div className="flag-fix-headline">No suggestion yet for this item.</div>
                    <div className="flag-fix-detail">
                      {item.raw
                        ? <>The plan supplied <code>{item.raw}</code> for this field. Ask the Sidekick agent to propose a value, or open the Plan Grid to set one yourself.</>
                        : <>This came from the plan validator, which reports what&apos;s missing but doesn&apos;t propose a value. Ask the Sidekick agent for one, or set it in the Plan Grid.</>}
                    </div>
                  </div>
                )}

                <div className="flag-ask">
                  <div className="flag-ask-label">
                    {suggestion ? 'Not what you wanted? Ask the AI assistant:' : 'Ask the AI assistant:'}
                  </div>
                  <div className="flag-ask-input-wrap">
                    <textarea
                      className="flag-ask-input"
                      rows={3}
                      placeholder={'e.g. "Pull a fresh sync from Snowflake instead"'}
                      value={flagAsk[item.id] || ''}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setFlagAsk((prev) => ({ ...prev, [item.id]: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); askAboutFlag(item); }
                      }}
                    />
                    <button
                      className="flag-ask-send"
                      title="Ask the Sidekick agent about this item"
                      onClick={(e) => { e.stopPropagation(); askAboutFlag(item); }}
                    >
                      <span className="material-icons">auto_awesome</span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="disc-actions" onClick={(e) => e.stopPropagation()}>
                {suggestion && (
                  <button className="btn-push-plan" onClick={() => applyFlagSuggestion(item)}>
                    ✓ Apply suggestion
                  </button>
                )}
                <button className="btn-accept-dsp" onClick={() => viewFlagInGrid(item)}>
                  ↗ View Plan Grid
                </button>
                <button className="btn-dismiss-disc" onClick={() => skipFlag(item.id)}>
                  Skip for now
                </button>
              </div>
            </div>
          </td>
        </tr>
      </React.Fragment>
    );
  };

  // Ask the Sidekick agent about this specific finding. Opens the sidebar so
  // the answer is visible; the session only exists for plan-backed campaigns.
  const askAboutFlag = (item) => {
    const question = (flagAsk[item.id] || '').trim();
    if (!agentSession) {
      toast('The Sidekick agent is unavailable for this campaign');
      return;
    }
    const scope = `${item.field} on ${item.rows.join(', ')}`;
    agentSession.sendMessage(
      question
        ? `About the flagged item "${item.msg}" (${scope}): ${question}`
        : `Suggest a fix for the flagged item "${item.msg}" (${scope}). `
          + 'Propose a concrete value and explain why.',
    );
    setFlagAsk((prev) => ({ ...prev, [item.id]: '' }));
    setAiOpen(true);
    toast('Asked the Sidekick agent — see the panel');
  };


  // Save/discard bar — rendered above the grid whenever edits are pending.
  const renderPlanNotices = () => (planNotices?.items?.length ? (
    <PlanNotices
      notices={planNotices.items}
      defaultSeverity={planNotices.severity}
      onDismiss={() => setPlanNotices(null)}
    />
  ) : null);

  const renderEditBar = () => (pendingEditCount === 0 ? null : (
    <div className="lcd-editbar">
      <span className="lcd-editbar-count">
        {pendingEditCount} unsaved change{pendingEditCount === 1 ? '' : 's'}
      </span>
      <button className="btn btn-secondary btn-sm" onClick={discardGridEdits} disabled={savingEdits}>Discard</button>
      <button className="btn btn-launch btn-sm" onClick={saveGridEdits} disabled={savingEdits}>
        {savingEdits ? 'Saving…' : 'Save changes'}
      </button>
    </div>
  ));

  // Cell detail panel — CLW's component, so the audiences/geo/scalar modals
  // are identical. The `clw` class supplies the CSS variables its .clw-*
  // styles read (tokens are declared on the .clw root).
  const renderCellPanel = (readOnly = false) => (
    <div className={`lcd-cell-panel-wrap clw ${detailRow ? 'open' : ''}`}>
      <CellDetailPanel
        detailRow={detailRow}
        detailCellField={detailCellField}
        isGt={isGtPlan}
        groupCols={gridGroupCols}
        groupKeys={gridGroupKeys}
        getRowValue={getRowValue}
        ctsReview={plan?.ctsReview}
        pendingDeletionsForRow={detailRow ? pendingDeletions[detailRow.rowId] : null}
        onToggleListDeletion={toggleListDeletion}
        listFields={gridListFields}
        pendingListOpsForRow={detailRow ? pendingListOps[detailRow.rowId] : null}
        onAddListItem={addListItem}
        onEditListItem={editListItem}
        onLookup={runLookup}
        onRefreshLookup={isGtPlan ? refreshLookup : undefined}
        onClose={closeCellPanel}
        alreadyLaunched={isYahooPlan && planAlreadyLaunched}
        editableFields={gridEditableFields}
        fieldEditors={gridFieldEditors}
        fieldOptions={gridFieldOptions}
        readOnlyReasons={gridReadOnlyReasons}
        editingCell={editingCell}
        setEditingCell={setEditingCell}
        editingDraft={editingDraft}
        setEditingDraft={setEditingDraft}
        commitCellEdit={commitCellEdit}
        cancelCellEdit={cancelCellEdit}
        editedRows={editedRows}
      />
    </div>
  );

  // Freshness label sitting under the header's Sync now button. `stale` drives
  // the amber treatment and matches the threshold the auto-refresh uses.
  const lastRefresh = (() => {
    if (view.isDemo) return { label: 'Last refresh 4 min ago', stale: false };
    if (reconSyncing) return { label: 'Refreshing…', stale: false };
    // Not "Never refreshed" — we don't know that until the state has loaded.
    if (!reconKnown) return { label: 'Checking sync status…', stale: false };
    if (!view.syncedAt) return { label: 'Never refreshed', stale: true };
    const ago = timeAgo(view.syncedAt);
    return {
      label: ago ? `Last refresh ${ago}` : 'Last refresh recently',
      stale: Date.now() - new Date(view.syncedAt).getTime() > STALE_SYNC_MS,
    };
  })();

  const remaining = Math.max(0, view.discs.filter((d) => !resolved[d.id]).length);
  // Demo only — mirrors the mock: strip starts 3 short of total and tracks
  // total − open discrepancies once the user starts resolving. With real
  // data there's no DSP readback yet, so the match stat says "pending".
  const anyResolved = Object.keys(resolved).length > 0;
  const demoMatch = Math.max(0, anyResolved ? view.totalAdGroups - remaining : view.totalAdGroups - 3);

  const toggleDisc = (id) => {
    if (resolved[id]) return;
    setOpenDiscs((prev) => {
      const wasOpen = prev.has(id);
      const next = new Set();
      if (!wasOpen) next.add(id); // one open at a time, like the design
      return next;
    });
  };

  const toggleAllDisc = () => {
    setOpenDiscs((prev) => {
      const unresolvedIds = view.discs.filter((d) => !resolved[d.id]).map((d) => d.id);
      const anyClosed = unresolvedIds.some((id) => !prev.has(id));
      return anyClosed ? new Set(unresolvedIds) : new Set();
    });
  };

  const resolveDisc = async (id, mode) => {
    // Guard the write path too, not just the button — the backend returns 403
    // regardless, but this keeps the demo view honest and avoids a pointless
    // round-trip.
    if (mode === 'push' && !pushToDspEnabled) {
      toast('⚠ Push to DSP is disabled for this account');
      return;
    }
    const d = view.discs.find((x) => x.id === id);
    if (view.isDemo) {
      const msg = (d && d[mode] && d[mode].msg)
        || (mode === 'dismiss' ? 'Discrepancy dismissed' : 'Discrepancy resolved');
      setResolved((prev) => ({ ...prev, [id]: mode }));
      setOpenDiscs(new Set());
      toast(`✓ ${msg}`);
      return;
    }
    try {
      const body = {
        accountId,
        userId,
        discrepancyId: id,
        mode,
        acceptFix: mode === 'accept' ? d?.accept?.fix : undefined,
        pushFix: mode === 'push' ? d?.push?.fix : undefined,
      };
      const res = await api.post(getApiUrl(`/api/v2/campaigns/${campaignId}/reconciliation/resolve`), body);
      setResolved(Object.fromEntries(
        Object.entries(res.data?.resolutions || {}).map(([k, v]) => [k, v.mode || 'resolved']),
      ));
      setOpenDiscs(new Set());
      toast(mode === 'push'
        ? '✓ Push dispatched to the DSP — re-sync in a few minutes to verify'
        : mode === 'dismiss'
          ? '✓ Discrepancy dismissed — plan and DSP left unchanged'
          : '✓ DSP value accepted into the plan');
    } catch (e) {
      toast(`⚠ ${e.response?.data?.error || 'Failed to resolve discrepancy'}`);
    }
  };

  // Only dismiss/flag are undoable — accept wrote a plan revision and push
  // dispatched a change to the live DSP, so neither can be walked back from
  // here. The backend enforces the same rule.
  const REOPENABLE_MODES = ['dismiss', 'flag'];
  const canReopen = (id) => REOPENABLE_MODES.includes(resolved[id]) && !signoff;

  const reopenDisc = async (id) => {
    if (view.isDemo) {
      setResolved((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      toast('↺ Discrepancy restored');
      return;
    }
    try {
      const res = await api.post(getApiUrl(`/api/v2/campaigns/${campaignId}/reconciliation/reopen`), {
        accountId, userId, discrepancyId: id,
      });
      setResolved(Object.fromEntries(
        Object.entries(res.data?.resolutions || {}).map(([k, v]) => [k, v.mode || 'resolved']),
      ));
      toast('↺ Discrepancy restored to the open list');
    } catch (e) {
      toast(`⚠ ${e.response?.data?.error || 'Failed to restore discrepancy'}`);
    }
  };

  const doSignoff = async () => {
    if (view.isDemo) {
      if (remaining > 0) {
        toast(`⚠ Resolve all ${remaining} discrepancies before signing off`);
        return;
      }
      toast('✓ QA signed off — reconciliation complete');
      return;
    }
    if (!view.synced) {
      toast('Run a DSP sync before signing off');
      return;
    }
    try {
      const res = await api.post(getApiUrl(`/api/v2/campaigns/${campaignId}/reconciliation/signoff`), { accountId, userId });
      setSignoff(res.data?.signoff || { at: new Date().toISOString() });
      toast('✓ QA signed off — reconciliation complete');
    } catch (e) {
      toast(`⚠ ${e.response?.data?.error || 'Failed to sign off'}`);
    }
  };

  // Full reconciliation report: a run header (what was compared, when, by
  // whom), the discrepancy ledger with how each one was settled, and the
  // ad-group plan-vs-DSP comparison. Built from `view`, which both the demo
  // and the synced server data normalize into — so the button produces a real
  // file in either mode rather than a placeholder toast.
  const RESOLUTION_LABELS = {
    push: 'Pushed plan value to DSP',
    accept: 'Accepted DSP value into plan',
    dismiss: 'Dismissed — not an issue',
    flag: 'Flagged for trafficker',
  };

  const buildReconciliationReport = () => {
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const row = (cells) => cells.map(esc).join(',');
    const pairs = (list) => (list || [])
      .map(([label, value]) => `${label}: ${value}`)
      .join('; ');
    // Server resolutions carry mode + timestamp + actor; the demo only tracks
    // the mode in local state. Fall back so both render the same columns.
    const detailFor = (id) => {
      const serverRes = recon?.resolutions?.[id];
      const mode = serverRes?.mode || resolved[id] || null;
      return {
        label: mode ? (RESOLUTION_LABELS[mode] || mode) : 'Open — unresolved',
        at: serverRes?.at ? new Date(serverRes.at).toISOString() : '',
      };
    };

    const total = view.discs.length;
    const open = view.discs.filter((d) => !resolved[d.id]).length;

    const lines = [
      row(['Reconciliation report']),
      row(['Campaign', title]),
      row(['DSP', view.dsp]),
      row(['Flight', view.flight || 'Not set']),
      row(['Ad groups', view.totalAdGroups]),
      row(['IO budget', view.budget]),
      row(['Impression goal', view.imps]),
      row(['DSP synced', view.syncedAt ? new Date(view.syncedAt).toISOString() : (view.isDemo ? 'Demo data' : 'Never synced')]),
      row(['Plan match', view.isDemo ? `${demoMatch}/${view.totalAdGroups}` : (view.synced ? `${view.matchCount}/${view.reconTotal}` : 'Awaiting sync')]),
      row(['Discrepancies', `${total} total, ${open} open, ${total - open} resolved`]),
      row(['QA signed off', signoff?.at ? new Date(signoff.at).toISOString() : 'No']),
      row(['Report generated', new Date().toISOString()]),
      '',
      row(['DISCREPANCIES']),
      row(['Severity', 'Issue', 'Plan', 'DSP', 'Likely cause', 'Plan detail', 'DSP detail', 'Resolution', 'Resolved at']),
      ...view.discs.map((d) => {
        const res = detailFor(d.id);
        return row([
          SEV_META[d.sev]?.label || d.sev,
          d.text,
          d.diff?.plan,
          d.diff?.dsp,
          d.explain,
          pairs(d.plan),
          pairs(d.dsp),
          res.label,
          res.at,
        ]);
      }),
      ...(view.discs.length === 0 ? [row(['No discrepancies — the DSP matches the plan.'])] : []),
      '',
      row(['AD GROUP COMPARISON']),
      row([
        'Package', 'Ad group', 'Tactic', 'Status',
        'Daily (plan)', 'Daily (DSP)', 'Budget (plan)', 'Budget (DSP)',
        'Audience (plan)', 'Audience (DSP)', 'Geo (plan)', 'Geo (DSP)',
        'Creative (plan)', 'Creative (DSP)', 'Bid (plan)', 'Bid (DSP)',
      ]),
      ...view.gridRows.map((g) => row([
        g.pkg, g.name, g.tactic, g.status,
        g.daily?.[0], g.daily?.[1], g.total?.[0], g.total?.[1],
        g.audience?.[0], g.audience?.[1], g.geo?.[0], g.geo?.[1],
        g.creative?.[0], g.creative?.[1], g.bid?.[0], g.bid?.[1],
      ])),
    ];
    return lines.join('\n');
  };

  const exportReconciliationCsv = () => {
    // The demo view carries a full mock dataset, so it exports for real too;
    // only an unsynced live campaign has nothing to compare against yet.
    if (!view.isDemo && !view.synced) {
      toast('Reconciliation report available after DSP sync — run Sync now first');
      return;
    }
    try {
      const slug = String(title || 'campaign')
        .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60) || 'campaign';
      const stamp = new Date().toISOString().slice(0, 10);
      const blob = new Blob([buildReconciliationReport()], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reconciliation-${slug}-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast('✓ Reconciliation report downloaded');
    } catch (e) {
      toast('⚠ Could not build the reconciliation report');
    }
  };

  const toggleCard = (key) => setCollapsedCards((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const title = campaign?.name || (view.isDemo ? 'Beltopia — March Brand Always-On' : 'Campaign');

  // ── QA report table (grouped Plan/DSP subcolumns) ──
  const rpt = view.qaReports[qaReport];
  const qaMarked = rpt.rows.map((r) => {
    const misIdx = new Set();
    r.plan.forEach((v, i) => { if (v !== r.dsp[i]) misIdx.add(i); });
    const rowMis = misIdx.size > 0 || (r.miss && r.miss !== 'No');
    return { ...r, misIdx, rowMis };
  });
  const qaRows = qaOnlyMis ? qaMarked.filter((r) => r.rowMis) : qaMarked;
  const qaPages = Math.max(1, Math.ceil(qaRows.length / QA_PAGE_SIZE));
  const qaPageSafe = Math.min(qaPage, qaPages - 1);
  const qaVisible = qaRows.slice(qaPageSafe * QA_PAGE_SIZE, (qaPageSafe + 1) * QA_PAGE_SIZE);

  const renderDiscRow = (d) => {
    const meta = SEV_META[d.sev];
    const isResolved = Boolean(resolved[d.id]);
    const isOpen = openDiscs.has(d.id) && !isResolved;
    return (
      <React.Fragment key={d.id}>
        <tr
          className={`disc-item issue-item ${isOpen ? 'open' : ''} ${isResolved ? 'resolved' : ''}`}
          onClick={() => toggleDisc(d.id)}
        >
          <td><span className={meta.dot} /></td>
          <td><span className={`issue-severity ${meta.cls}`}>{meta.label}</span></td>
          <td className="issue-text">
            {d.text}
            {isResolved && (
              <span className="disc-resolved-badge">
                {/* 'flag' is no longer an action this page offers, but rows
                    flagged before it was removed still read back that way. */}
                {resolved[d.id] === 'push' ? '✓ Pushed'
                  : resolved[d.id] === 'accept' ? '✓ Accepted'
                    : resolved[d.id] === 'dismiss' ? '✓ Dismissed'
                      : resolved[d.id] === 'flag' ? '⚑ Flagged' : '✓ Resolved'}
              </span>
            )}
            {/* Undo is offered only for the bookkeeping-only resolutions.
                Accept/push changed the plan or the live DSP, so there's
                nothing safe to revert from here. */}
            {isResolved && canReopen(d.id) && (
              <button
                className="disc-undo-btn"
                title="Put this discrepancy back in the open list"
                onClick={(e) => { e.stopPropagation(); reopenDisc(d.id); }}
              >
                ↺ Undo
              </button>
            )}
          </td>
          <td>
            <span className="disc-diff">
              <span className="plan-v">{d.diff.plan}</span> <span className="arrow">→</span> <span className="dsp-v">{d.diff.dsp}</span>
            </span>
          </td>
          <td><span className="material-icons issue-row-chevron">keyboard_arrow_down</span></td>
        </tr>
        <tr className={`disc-detail-row issue-detail-row ${isOpen ? 'open' : ''}`}>
          <td colSpan={5}>
            <div className="disc-detail">
              <div className="disc-compare">
                <div className="disc-compare-col dc-plan">
                  <div className="disc-compare-head">Plan (Stellair)</div>
                  <div className="disc-compare-body">
                    {d.plan.map(([label, value, mis]) => (
                      <React.Fragment key={label}>
                        <span className="label">{label}</span>
                        <span className={mis ? 'v-mismatch' : undefined}>{value}</span>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
                <div className="disc-compare-col dc-dsp">
                  <div className="disc-compare-head">
                    DSP ({view.dsp}){d.dspHeadNote && <span className="disc-compare-head-note"> {d.dspHeadNote}</span>}
                  </div>
                  <div className="disc-compare-body">
                    {d.dsp.map(([label, value, mis]) => (
                      <React.Fragment key={label}>
                        <span className="label">{label}</span>
                        <span className={mis ? 'v-mismatch' : undefined}>{value}</span>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
              <div className="disc-explain">
                <div className="eyebrow"><span className="material-icons">auto_awesome</span> Likely cause</div>
                {d.explain}
              </div>
              {/* Two actions only, per the design: push the plan value to the
                  DSP, or accept the DSP value into the plan. Push writes to the
                  live platform, so it only appears when the account's
                  'enable-v3-ui-launch-push-to-dsp' setting covers this platform — otherwise
                  we say why rather than leaving a gap where the button was. */}
              <div className="disc-actions" onClick={(e) => e.stopPropagation()}>
                {d.push && (pushToDspEnabled ? (
                  <button className="btn-push-plan" onClick={() => resolveDisc(d.id, 'push')}>{d.push.label}</button>
                ) : (
                  <span className="disc-push-disabled" title={`Ask an account admin to enable the '${PUSH_TO_DSP_KEY}' setting for this platform.`}>
                    ↑ Push to DSP is disabled for this account
                  </span>
                ))}
                {d.accept && (
                  <button className="btn-accept-dsp" onClick={() => resolveDisc(d.id, 'accept')}>{d.accept.label}</button>
                )}
                {/* Close it out without writing anywhere — for differences that
                    are expected and just need clearing so sign-off can proceed. */}
                <button className="btn-dismiss-disc" onClick={() => resolveDisc(d.id, 'dismiss')}>
                  ✕ Dismiss — not an issue
                </button>
              </div>
            </div>
          </td>
        </tr>
      </React.Fragment>
    );
  };

  // The board no longer sends a mode param — pick the view from the
  // campaign's stage, falling back to planning-review's alreadyLaunched flag
  // when the campaign isn't on the board list.
  const stage = campaign?.stage;
  const openClw = () => navigate(`/campaign/v3/start?campaign=${encodeURIComponent(campaignId || '')}`);
  const planStage = !demoMode && plan && (
    stage === 'plan_ready' || stage === 'plan_in_review'
    || (stage !== 'dsp_ready' && !plan.alreadyLaunched)
  );

  if (!demoMode && (planLoading || campaignLoading) && !plan) {
    // Brief data fetch — avoid flashing demo numbers as if they were real.
    return (
      <div className="launch-campaign-details">
        <div className="lcd-topbar">
          <button className="tb-back-btn" onClick={() => navigate('/launch')}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9,2 4,7 9,12" /></svg>
            Back to Launch
          </button>
        </div>
        <LoadingCard
          title="Loading campaign…"
          sub="Fetching the parsed plan and this campaign's current stage."
        />
      </div>
    );
  }

  if (!demoMode && !plan) {
    // No parsed plan (still processing, a platform with no review builders —
    // e.g. google-ads-display, Amazon DSP — or a pre-launcher campaign) —
    // link through to CLW instead of showing demo numbers.
    return (
      <div className="launch-campaign-details">
        <div className="lcd-topbar">
          <button className="tb-back-btn" onClick={() => navigate('/launch')}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9,2 4,7 9,12" /></svg>
            Back to Launch
          </button>
        </div>
        <div className="lcd-empty">
          {stage === 'processing'
            ? <>This campaign is still processing.{' '}</>
            : <>No parsed plan available for this campaign yet.{' '}</>}
          <a href="#clw" onClick={(e) => { e.preventDefault(); openClw(); }}>Open it in the Campaign Launch Workflow →</a>
        </div>
      </div>
    );
  }

  // ── Plan-review view (plan_ready / plan_in_review campaigns) ──
  if (planStage) {
    const rows = plan.rows;
    const summary = plan.summary;
    const ready = campaign?.stage === 'plan_ready';
    const dspLabel = platformLabel(campaign?.destination || summary?.platform);
    const totalAg = summary?.adGroupCount ?? rows.length;
    const { flagged, qaRowsPlan, formatRows, cleanRows, qaFlaggedCount } = planDerived;
    const pkgCount = summary?.packageCount || planDerived.pkgCount;
    const questions = planDerived.questionCount || summary?.warningCount || 0;
    const missing = planDerived.missingCount || summary?.errorCount || 0;
    const autoPct = rows.length ? Math.round((cleanRows / rows.length) * 100) : null;
    const flightLabel = summary?.flightStart && summary?.flightEnd ? `${summary.flightStart} – ${summary.flightEnd}` : null;
    const planBudget = resolveCampaignBudget(summary, campaign, rows);
    // Google Ads and Facebook are bought-media platforms, not DSPs, and their
    // plans are campaign-budgeted rather than IO-budgeted — say so instead of
    // printing "Google Ads Search DSP" / "Facebook DSP" and "IO budget".
    const isBoughtMedia = isGadsPlan || isFbPlan;
    const platformSuffix = isBoughtMedia ? dspLabel : `${dspLabel} DSP`;
    const budgetLabel = (isBoughtMedia || isYahooPlan) ? 'Campaign budget' : 'IO budget';
    // Neither platform's plan carries a CPM or an impression goal (both row
    // builders publish cpm/imps as null), so the format table drops those two
    // columns rather than rendering a pair of em-dash stripes.
    const noImpressionPricing = isBoughtMedia;

    return (
      <div className="launch-campaign-details">
        <div className="lcd-topbar">
          <button className="tb-back-btn" onClick={() => navigate('/launch')}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9,2 4,7 9,12" /></svg>
            Back to Launch
          </button>
          <div className="lcd-topbar-right">{agentButton}</div>
        </div>

        {/* Plan review header — camp-* component from campaign-header.css */}
        <div className="reconcile-summary-section plan-summary-section">
          <div className="rc-header">
            <div className="camp-status-row">
              <div className={`camp-status ${ready ? 'ws-status-ready' : 'ws-status-review'}`}>
                <div className="ws-dot" />
                <span>{ready ? 'Plan: Ready for review' : 'Plan: In review'}</span>
              </div>
              <div className="camp-header-right">
                {/* Re-resolve every audience against the live TTD / DV360 API.
                    Plan review only — the backend 409s once the campaign is
                    launched. The page stays interactive while it runs. */}
                {canRefreshAudiences && (
                  <span className={`lcd-aud-refresh --${audRefresh.status}`}>
                    {audRefresh.status === 'running' ? (
                      <span className="lcd-aud-refresh-msg">
                        <span className="lcd-aud-refresh-spin" aria-hidden="true">⟳</span> Refreshing audiences…
                      </span>
                    ) : (
                      <>
                        {audRefresh.status === 'done' && typeof audRefresh.total === 'number' && (
                          <span className="lcd-aud-refresh-msg">✓ {audRefresh.matched}/{audRefresh.total} matched</span>
                        )}
                        {audRefresh.status === 'error' && (
                          <span className="lcd-aud-refresh-msg err" title={audRefresh.error}>Couldn&apos;t refresh</span>
                        )}
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={refreshAudiences}
                          title="Re-resolve every audience against the platform"
                        >
                          {audRefresh.status === 'error' ? '↻ Retry audiences' : '↻ Refresh audiences'}
                        </button>
                      </>
                    )}
                  </span>
                )}
                {/* No "Parse report" or "Open in CLW" here — this page now
                    covers the review, edit and launch flow itself. GroundTruth
                    still routes to CLW through the launch button below, since
                    its launch needs the original upload only CLW holds. */}
                {canLaunchHere ? (
                  <button
                    className="btn btn-launch"
                    onClick={approveAndLaunch}
                    disabled={launching || launchSuccess}
                    title={pendingEditCount > 0
                      ? `Saves ${pendingEditCount} pending edit${pendingEditCount === 1 ? '' : 's'} first, then launches`
                      : ''}
                  >
                    {launchSuccess ? '✓ Launched'
                      : launching ? 'Launching…'
                      : 'Approve & launch →'}
                  </button>
                ) : (
                  <button
                    className="btn btn-launch"
                    onClick={openClw}
                    title={isGtPlan
                      ? 'GroundTruth launches from CLW, which holds the uploaded source file'
                      : 'Launch from CLW'}
                  >
                    Review &amp; approve in CLW →
                  </button>
                )}
              </div>
            </div>
            <div className="camp-header-row">
              <div className="camp-title">{campaign?.name || summary?.campaignName || 'Campaign'}</div>
            </div>
            <div className="camp-sub">
              {pkgCount ? `${countLabel(pkgCount, parentNoun)} · ` : ''}{countLabel(totalAg, rowNoun)}
              {flightLabel ? ` · Flight ${flightLabel}` : ''} · {platformSuffix}
            </div>
          </div>

          <div className="camp-summary-strip">
            <div className="camp-stat">
              <div className="camp-stat-val">{fmtInt(totalAg)}</div>
              <div className="camp-stat-lbl">{rowNounTitle}</div>
              <div className="camp-stat-sub">{pkgCount ? countLabel(pkgCount, parentNoun) : 'From campaign plan'}</div>
            </div>
            <div className="camp-stat">
              <div className="camp-stat-val">{planBudget.text}</div>
              <div className="camp-stat-lbl">{budgetLabel}</div>
              <div className="camp-stat-sub">{summary?.totalImpressions ? `${fmtInt(summary.totalImpressions)} impressions` : planBudget.sub}</div>
            </div>
            <div className="camp-stat">
              <div className="camp-stat-val ok">{fmtInt(cleanRows)}</div>
              <div className="camp-stat-lbl">Parsed clean</div>
              <div className="camp-stat-sub">No action needed</div>
            </div>
            <div className="camp-stat">
              <div className="camp-stat-val warn">{fmtInt(questions)}</div>
              <div className="camp-stat-lbl">Questions</div>
              <div className="camp-stat-sub">Stellair needs input</div>
            </div>
            <div className="camp-stat">
              <div className="camp-stat-val err">{fmtInt(missing)}</div>
              <div className="camp-stat-lbl">Missing data</div>
              <div className="camp-stat-sub">Required fields empty</div>
            </div>
            <div className="camp-stat">
              <div className="camp-stat-val ok">{autoPct != null ? `${autoPct}%` : '—'}</div>
              <div className="camp-stat-lbl">Auto-populated</div>
              <div className="camp-stat-sub">{autoPct != null ? `${100 - autoPct}% need your input` : 'From campaign plan'}</div>
            </div>
          </div>
        </div>

        {/* Plan tabs */}
        <div className="pane-tabs">
          <button className={`pane-tab-btn ${tab === 'qa' ? 'active' : ''}`} onClick={() => setTab('qa')}>QA Summary Stats</button>
          <button className={`pane-tab-btn ${tab === 'disc' ? 'active' : ''}`} onClick={() => setTab('disc')}>
            <span className="material-icons pane-tab-icon">warning_amber</span> {flagged.length} Flagged Items
          </button>
          <button className={`pane-tab-btn ${tab === 'grid' ? 'active' : ''}`} onClick={() => setTab('grid')}>Full {rowSingularHyphen} grid</button>
        </div>

        {/* Tab: QA Summary Stats — format breakdown + plan QA table */}
        {tab === 'qa' && (
          <div className="pane-tab-panel rc-qa-panel">
            <div className="rc-card">
              <div className="rc-card-head">
                <div>
                  <h3 className="rc-card-title">Format breakdown</h3>
                  <div className="rc-card-sub">Parsed plan totals by format</div>
                </div>
              </div>
              <div className="rc-card-body">
                <table className="cmp-tbl">
                  <thead>
                    <tr>
                      <th>Format</th>
                      <th className="num">{rowNounTitle}</th>
                      <th className="num">{parentNounTitle}</th>
                      <th className="num">Budget</th>
                      {/* Google Ads plans are priced per click / conversion,
                          Facebook plans per objective — neither states a plan
                          CPM or an impression goal, so both drop the columns. */}
                      {!noImpressionPricing && <th className="num">CPM</th>}
                      {!noImpressionPricing && <th className="num">Impressions</th>}
                      <th>Flight</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formatRows.map((r) => (
                      <tr key={r.tag}>
                        <td><span className={`cmp-tag ${r.tagCls}`}>{r.tag}</span></td>
                        <td className="num">{r.ag}</td>
                        <td className="num">{r.pkgs}</td>
                        <td className="num">{r.budget}</td>
                        {!noImpressionPricing && <td className="num">{r.cpm}</td>}
                        {!noImpressionPricing && <td className="num">{r.imps}</td>}
                        <td className="lcd-nowrap">{r.flight}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="rc-card">
              <div className="rc-card-head">
                <div>
                  <h3 className="rc-card-title">QA report</h3>
                  <div className="rc-card-sub">Per-{rowSingularHyphen} creative &amp; geo coverage from the parsed plan</div>
                </div>
                <div className="rc-qa-controls">
                  <span className="ps-table-badge badge-warn">{qaFlaggedCount ? `${qaFlaggedCount} flagged` : 'All clean'}</span>
                </div>
              </div>
              <div className="rc-card-body">
                <div className="ps-tbl-scroll">
                  <table className="cmp-tbl rc-qa-table">
                    <thead>
                      <tr>
                        <th>{rowSingularTitle}</th>
                        <th className="num">Creative assets</th>
                        <th className="num">Geo targets</th>
                        <th className="num">Issues</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {qaRowsPlan.map((r) => (
                        <tr key={r.name} className={r.issues > 0 ? 'mismatch' : undefined}>
                          <td>{r.name}</td>
                          <td className="num">{fmtInt(r.creatives)}</td>
                          <td className="num">{fmtInt(r.geos)}</td>
                          <td className="num">{r.issues || '—'}</td>
                          <td>
                            {r.issues > 0
                              ? <span className="cmp-tag" style={TAG_STYLES.mis1}>Needs input</span>
                              : <span className="cmp-tag" style={TAG_STYLES.match}>Clean</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab: Flagged Items — real validation issues from CLW's annotations */}
        {tab === 'disc' && (
          <div className="pane-tab-panel">
            <div className="disc-bar">
              {flagged.length === 0 ? (
                <div className="lcd-empty">
                  No flagged items — the plan parsed clean.
                  {(summary?.issueCount || 0) > 0 && (
                    <> {summary.issueCount} campaign-level issue{summary.issueCount === 1 ? '' : 's'} noted —{' '}
                    <a href="#clw" onClick={(e) => { e.preventDefault(); openClw(); }}>see details in CLW →</a></>
                  )}
                </div>
              ) : (
                <>
                  <div className="disc-bar-header">
                    <div />
                    <button className="disc-expand-all" onClick={() => toggleAllFlags(flagged)}>
                      {flagged.filter((i) => !skippedFlags.has(i.id)).every((i) => openFlags.has(i.id))
                        && flagged.some((i) => !skippedFlags.has(i.id))
                        ? 'Collapse all' : 'Expand all'}
                    </button>
                  </div>
                  <table className="disc-list issue-list">
                    <thead>
                      <tr className="issue-table-head">
                        <th />
                        <th>Type</th>
                        <th>Issue</th>
                        <th>Field</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {flagged.map((i) => renderFlaggedRow(i))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>
        )}

        {/* Tab: Full ad-group grid (plan values) */}
        {tab === 'grid' && (
          <div className="pane-tab-panel rc-grid-panel">
            <div className="rc-grid-wrap">
              {/* Same CLW review grid the DSP-reconcile view renders — column
                  group tabs, cell annotations, resizable sticky columns and
                  click-to-edit. Here there is no DSP sync, so the annotations
                  are purely the plan's own validation findings. */}
              {renderPlanNotices()}
              {renderEditBar()}
              {renderClwGrid(false)}
            </div>
          </div>
        )}

        {/* ReviewGrid mounts its own detail panel on the grid tab. */}
        {tab !== 'grid' && renderCellPanel(false)}
        {renderAgentSidebar('plan')}
        {toastMsg && <div className="lcd-toast">{toastMsg}</div>}
      </div>
    );
  }

  return (
    <div className="launch-campaign-details">
      <div className="lcd-topbar">
        <button className="tb-back-btn" onClick={() => navigate('/launch')}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9,2 4,7 9,12" /></svg>
          Back to Launch
        </button>
        <div className="lcd-topbar-right">{agentButton}</div>
      </div>

      {/* DSP reconcile header — camp-* component from campaign-header.css */}
      <div className="reconcile-summary-section">
        <div className="rc-header">
          <div className="camp-status-row">
            <div className="camp-status ws-status-live">
              <div className="ws-dot" />
              <span>{reconcileSupported ? 'DSP: Ready for review' : 'Launched'}</span>
            </div>
            <div className="camp-header-right">
              {/* Caption sits inline BEFORE the button so this control is one
                  line tall — a stacked caption made the block two lines and
                  knocked its button out of line with the other two.
                  Both controls need a reconciliation to act on. */}
              {reconcileSupported && (
                <>
                  <div className="camp-sync-control">
                    <span className={`camp-sync-age${lastRefresh.stale ? ' stale' : ''}`}>{lastRefresh.label}</span>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => (view.isDemo ? toast('Resyncing from DSP…') : syncNow())}
                      disabled={reconSyncing}
                    >
                      {reconSyncing ? '⟳ Syncing…' : '↻ Sync now'}
                    </button>
                  </div>
                  <button className="btn btn-secondary btn-sm" onClick={exportReconciliationCsv}>↓ Reconciliation report</button>
                </>
              )}
              <button className="btn btn-launch" onClick={doSignoff} disabled={Boolean(signoff)}>
                {signoff
                  ? '✓ QA signed off'
                  : (remaining === 0 && (view.isDemo || view.synced)
                    ? '✓ All match — sign off & close QA →'
                    : 'Sign off & close QA →')}
              </button>
            </div>
          </div>
          <div className="camp-header-row">
            <div className="camp-title">{title}</div>
          </div>
          <div className="camp-sub">
            Launched to {view.dsp} · {countLabel(view.totalAdGroups, rowNoun)}
            {view.flight ? ` · Flight ${view.flight}` : ''}
            {/* Sync status only — the Sync now control lives in the header
                button row next to Reconciliation report / Sign off. Platforms
                without a reconcile handler say nothing about syncing. */}
            {!reconcileSupported ? '' : view.isDemo
              ? ' · DSP synced 4 min ago'
              : (view.synced
                ? ` · DSP synced ${timeAgo(view.syncedAt) || 'recently'}`
                : (reconSyncing ? ' · DSP sync in progress…' : ' · DSP not synced yet'))}
          </div>
        </div>

        <div className="camp-summary-strip">
          <div className="camp-stat">
            <div className="camp-stat-val">{fmtInt(view.totalAdGroups)}</div>
            <div className="camp-stat-lbl">{rowNounTitle} live</div>
            <div className="camp-stat-sub">In {view.dsp}{reconcileSupported ? ' DSP' : ''}</div>
          </div>
          <div className="camp-stat ok">
            {view.isDemo ? (
              <>
                <div className="camp-stat-val">{demoMatch}<span className="camp-stat-denom">/{view.totalAdGroups}</span></div>
                <div className="camp-stat-lbl">Match plan</div>
                <div className="camp-stat-sub">{((demoMatch / view.totalAdGroups) * 100).toFixed(1)}% accurate</div>
              </>
            ) : view.synced ? (
              <>
                <div className="camp-stat-val">{view.matchCount}<span className="camp-stat-denom">/{view.reconTotal}</span></div>
                <div className="camp-stat-lbl">Match plan</div>
                <div className="camp-stat-sub">
                  {view.reconTotal > 0 ? `${((view.matchCount / view.reconTotal) * 100).toFixed(1)}% accurate` : '—'}
                </div>
              </>
            ) : (
              <>
                <div className="camp-stat-val">—</div>
                <div className="camp-stat-lbl">Match plan</div>
                <div className="camp-stat-sub">Awaiting DSP sync</div>
              </>
            )}
          </div>
          <div className="camp-stat">
            <div className="camp-stat-val">{view.budget}</div>
            <div className="camp-stat-lbl">{view.isDemo ? 'In flight' : 'IO budget'}</div>
            <div className="camp-stat-sub">{view.budgetSub}</div>
          </div>
          <div className="camp-stat">
            <div className="camp-stat-val">{view.imps}</div>
            <div className="camp-stat-lbl">{view.isDemo ? 'Imp goal (DSP)' : 'Imp goal (plan)'}</div>
            <div className="camp-stat-sub">{view.impsSub}</div>
          </div>
        </div>
      </div>

      {/* Outer pane tabs */}
      <div className="pane-tabs">
        <button className={`pane-tab-btn ${tab === 'qa' ? 'active' : ''}`} onClick={() => setTab('qa')}>QA Summary Stats</button>
        <button className={`pane-tab-btn ${tab === 'disc' ? 'active' : ''}`} onClick={() => setTab('disc')}>
          <span className="material-icons pane-tab-icon">info_outline</span> {(view.isDemo || view.synced) ? remaining : '—'} Discrepancies
        </button>
        <button className={`pane-tab-btn ${tab === 'grid' ? 'active' : ''}`} onClick={() => setTab('grid')}>Full ad-group grid</button>
      </div>

      {/* Tab: QA Summary Stats */}
      {tab === 'qa' && (
        <div className="pane-tab-panel rc-qa-panel">
          <div className={`rc-card ${collapsedCards.has('tactic') ? 'collapsed' : ''}`}>
            <div className="rc-card-head" onClick={() => toggleCard('tactic')}>
              <div>
                <h3 className="rc-card-title">Tactic-level summary</h3>
                <div className="rc-card-sub">
                  {view.isDemo || view.synced
                    ? 'Plan vs. live DSP totals, grouped by tactic'
                    : 'Plan totals by tactic — DSP columns mirror the plan until a sync runs'}
                </div>
              </div>
              <div className="rc-card-legend">
                <span><span className="dot dot-match" />Match</span>
                <span><span className="dot dot-mis" />Mismatch</span>
              </div>
              <span className="material-icons rc-card-chevron">expand_less</span>
            </div>
            <div className="rc-card-body">
              <table className="cmp-tbl tactic-tbl">
                <thead>
                  <tr>
                    <th>Tactic</th>
                    <th className="num">Ad groups (Plan)</th>
                    <th className="num col-plan">Plan budget</th>
                    <th className="num col-dsp">DSP budget</th>
                    <th className="num col-diff">Δ Budget</th>
                    <th className="num col-plan">Plan impressions</th>
                    <th className="num col-dsp">DSP impressions</th>
                    <th className="num col-diff">Δ Imps</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {view.tacticRows.map((r) => (
                    <tr key={r.tag} className={r.mismatch ? 'mismatch' : undefined}>
                      <td><span className={`cmp-tag ${r.tagCls}`}>{r.tag}</span> {r.name}</td>
                      <td className="num">{r.ag}</td>
                      <td className="num col-plan">{r.planB}</td>
                      <td className="num col-dsp">{r.dspB}</td>
                      <td className={`num col-diff ${r.dBCls}`}>{r.dB}</td>
                      <td className="num col-plan">{r.planI}</td>
                      <td className="num col-dsp">{r.dspI}</td>
                      <td className={`num col-diff ${r.dICls}`}>{r.dI}</td>
                      <td><span className="cmp-tag" style={r.statusStyle}>{r.status}</span></td>
                    </tr>
                  ))}
                  <tr className="total">
                    <td>TOTAL</td>
                    <td className="num">{view.tacticTotal.ag}</td>
                    <td className="num col-plan">{view.tacticTotal.planB}</td>
                    <td className="num col-dsp">{view.tacticTotal.dspB}</td>
                    <td className={`num col-diff ${view.tacticTotal.dBCls}`}>{view.tacticTotal.dB}</td>
                    <td className="num col-plan">{view.tacticTotal.planI}</td>
                    <td className="num col-dsp">{view.tacticTotal.dspI}</td>
                    <td className={`num col-diff ${view.tacticTotal.dICls}`}>{view.tacticTotal.dI}</td>
                    <td>
                      {view.isDemo
                        ? <strong className="tactic-total-status">{view.tacticTotal.status}</strong>
                        : <span className="cmp-tag" style={view.tacticTotal.statusStyle}>{view.tacticTotal.status}</span>}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className={`rc-card ${collapsedCards.has('setup') ? 'collapsed' : ''}`}>
            <div className="rc-card-head" onClick={() => toggleCard('setup')}>
              <div>
                <h3 className="rc-card-title">Targeting &amp; setup summary</h3>
                <div className="rc-card-sub">Audience attachments, geos, and creative match between plan and DSP</div>
              </div>
              <span className="material-icons rc-card-chevron">expand_less</span>
            </div>
            <div className="rc-card-body">
              <table className="cmp-tbl tactic-tbl">
                <thead>
                  <tr>
                    <th>Setting</th>
                    <th className="col-plan">Plan</th>
                    <th className="col-dsp">DSP</th>
                    <th>Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {view.setupRows.map((r) => (
                    r.section
                      ? <tr key={`s-${r.section}`} className="section"><td colSpan={4}>{r.section}</td></tr>
                      : (
                        <tr key={r.name} className={r.ok ? undefined : 'mismatch'}>
                          <td>{r.name}</td>
                          <td className="col-plan">{r.plan}</td>
                          <td className="col-dsp">{r.dsp}</td>
                          <td className={`col-diff ${r.cls || (r.ok ? 'ok' : 'bad')}`}>
                            {r.cls === 'neutral'
                              ? <span className="cmp-tag" style={TAG_STYLES.pending}>{r.delta}</span>
                              : r.delta}
                          </td>
                        </tr>
                      )
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className={`rc-card ${collapsedCards.has('qa-reports') ? 'collapsed' : ''}`}>
            <div className="rc-card-head" onClick={() => toggleCard('qa-reports')}>
              <div>
                <h3 className="rc-card-title">QA reports</h3>
                <div className="rc-card-sub">Per-ad-group plan vs DSP — mismatched cells flagged</div>
              </div>
              <div className="rc-qa-controls" onClick={(e) => e.stopPropagation()}>
                <select
                  className="ps-table-select"
                  value={qaReport}
                  onChange={(e) => { setQaReport(e.target.value); setQaPage(0); }}
                >
                  <option value="creatives">Creative coverage — by ad group</option>
                  <option value="locations">Location filters — geo assignment</option>
                </select>
                <span className="ps-table-badge badge-warn">{rpt.badge}</span>
              </div>
              <span className="material-icons rc-card-chevron">expand_less</span>
            </div>
            <div className="rc-card-body">
              <div className="ps-tbl-scroll">
                <table className="cmp-tbl rc-qa-table">
                  <thead>
                    <tr>
                      {rpt.cols.map((c) => (
                        c.paired
                          ? <th key={c.key} colSpan={2} className="num qa-th-group">{c.label}</th>
                          : <th key={c.key} rowSpan={2} className="qa-th-plain">{c.label}</th>
                      ))}
                    </tr>
                    <tr>
                      {rpt.cols.filter((c) => c.paired).map((c) => (
                        <React.Fragment key={c.key}>
                          <th className="num col-plan">Plan</th>
                          <th className="num col-dsp">DSP</th>
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {qaVisible.map((r) => (
                      <tr key={r.name} className={r.rowMis ? 'mismatch' : undefined}>
                        <td>{r.name}</td>
                        {r.plan.map((v, i) => (
                          <React.Fragment key={i}>
                            <td className="num col-plan">{Number(v).toLocaleString()}</td>
                            <td className={`num col-dsp ${r.misIdx.has(i) ? 'cell-mis' : ''}`}>{Number(r.dsp[i]).toLocaleString()}</td>
                          </React.Fragment>
                        ))}
                        {'miss' in r && <td className={r.miss !== 'No' ? 'cell-mis-note' : undefined}>{r.miss}</td>}
                        <td>
                          {r.rowMis
                            ? <span className="cmp-tag" style={TAG_STYLES.mis2}>Mismatch</span>
                            : (view.isDemo || r.compared)
                              ? <span className="cmp-tag" style={TAG_STYLES.match}>Match</span>
                              : <span className="cmp-tag" style={TAG_STYLES.pending}>{view.synced ? 'Not compared' : 'Sync pending'}</span>}
                        </td>
                      </tr>
                    ))}
                    <tr className="total">
                      <td>TOTAL</td>
                      {rpt.total.plan.map((v, i) => (
                        <React.Fragment key={i}>
                          <td className="num col-plan">{Number(v).toLocaleString()}</td>
                          <td className={`num col-dsp ${v !== rpt.total.dsp[i] ? 'cell-mis' : ''}`}>{Number(rpt.total.dsp[i]).toLocaleString()}</td>
                        </React.Fragment>
                      ))}
                      {'miss' in (rpt.rows[0] || {}) && <td />}
                      <td />
                    </tr>
                  </tbody>
                </table>
              </div>
              {qaPages > 1 && (
                <div className="qa-pagination">
                  <button className="qa-pg-btn" disabled={qaPageSafe === 0} onClick={() => setQaPage(qaPageSafe - 1)}>← Prev</button>
                  <span className="qa-pg-info">Page {qaPageSafe + 1} of {qaPages}</span>
                  <button className="qa-pg-btn" disabled={qaPageSafe >= qaPages - 1} onClick={() => setQaPage(qaPageSafe + 1)}>Next →</button>
                </div>
              )}
              <div className="ps-tbl-footer">
                <div className="ps-tbl-legend"><div className="leg-dot leg-dot-mis" /> Mismatched cell</div>
                <div className="ps-tbl-legend"><div className="leg-dot leg-dot-row" /> Row with mismatch</div>
                <div className="ps-tbl-legend"><div className="leg-dot leg-dot-match" /> Match</div>
                <div className="ps-tbl-footer-right">
                  <label>
                    <input
                      type="checkbox"
                      checked={qaOnlyMis}
                      onChange={(e) => { setQaOnlyMis(e.target.checked); setQaPage(0); }}
                    />
                    Only show mismatched rows
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab: Discrepancies */}
      {tab === 'disc' && (
        <div className="pane-tab-panel">
          <div className="disc-bar">
            {view.discs.length === 0 ? (
              <div className="lcd-empty">
                {view.synced
                  ? <>No discrepancies — the DSP matches the plan. Synced {timeAgo(view.syncedAt) || 'recently'}.</>
                  : !reconcileSupported
                    // Google Ads has no reconcile handler, so don't point the
                    // user at a sync that can't run.
                    ? <>Plan-vs-live comparison isn&apos;t available for {view.dsp} yet. The grid below shows the plan as launched.</>
                    : (
                      <>
                        No discrepancies to show yet — run a DSP sync to compare the plan against live state.{' '}
                        <a href="#sync" onClick={(e) => { e.preventDefault(); syncNow(); }}>
                          {reconSyncing ? '⟳ Syncing…' : '↻ Sync now'}
                        </a>
                      </>
                    )}
              </div>
            ) : (
              <>
                <div className="disc-bar-header">
                  <div />
                  <button className="disc-expand-all" onClick={toggleAllDisc}>
                    {view.discs.filter((d) => !resolved[d.id]).every((d) => openDiscs.has(d.id)) && remaining > 0 ? 'Collapse all' : 'Expand all'}
                  </button>
                </div>
                <table className="disc-list issue-list">
                  <thead>
                    <tr className="disc-table-head issue-table-head">
                      <th />
                      <th>Severity</th>
                      <th>Issue:</th>
                      <th>Plan → DSP</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {view.discs.map(renderDiscRow)}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </div>
      )}

      {/* Tab: Full ad-group grid */}
      {tab === 'grid' && (
        <div className="pane-tab-panel rc-grid-panel">
          <div className="rc-grid-wrap">
            {/* No section header here — the grid's own toolbar and column-group
                tabs already say what this is, and the heading only pushed the
                grid down. Ad-group and sync counts live in the page header. */}
            {/* Read-only: the campaign is already live, so the plan has been
                pushed and editing it here would diverge the two. No edit bar
                for the same reason — nothing can become pending.
                DSP differences arrive as annotations (see gridRowsForClw),
                so mismatches still highlight in the grid's own language. */}
            {renderClwGrid(true)}
          </div>
        </div>
      )}

      {tab !== 'grid' && renderCellPanel(true)}
      {renderAgentSidebar('recon')}
      {toastMsg && <div className="lcd-toast">{toastMsg}</div>}
    </div>
  );
};

export default LaunchCampaignDetails;
